// src/views/accueil/BlocOperateur.jsx — L'OPÉRATEUR, VOIX DE L'ACCUEIL.
//
// Décision Dimitri, 2026-09-07 : « J'ouvre l'accueil le matin, pas l'opérateur.
// Donc c'est là que ses alertes doivent vivre. »
//
// ── SON STYLE, PAS CELUI DU BANDEAU ────────────────────────────────────────
// Bloc blanc en dégradé, sans bordure, sa sphère et son nom en tête (version C
// de la maquette, 2026-09-13). Le bandeau navy est juste au-dessus : deux blocs
// de même teinte se liraient comme un seul, et on perdrait ce qui distingue SA
// voix de l'écran.
//
// ── CE QU'IL NE FAIT PAS ───────────────────────────────────────────────────
// Il n'affiche JAMAIS un compte qu'il ne sait pas. Zéro proposition ⇒ pas de
// ligne d'accord. Opérateur dormant ⇒ il le dit (règle déjà en vigueur sur cet
// écran : « l'opérateur qui dort se dit aussi »).
//
// ── LE CHAMP DE RÉPONSE ────────────────────────────────────────────────────
// Il est MONTÉ AVANT le retrait du micro flottant, jamais l'inverse (consigne
// Dimitri : « ne retire rien avant d'avoir le remplaçant »). Tant que la
// conversation n'est pas un lot ouvert, il DIT qu'il ne répond pas encore —
// un champ qui avale ce qu'on lui écrit serait pire que pas de champ.

import { useState, useEffect, useRef } from "react";
import { DS } from "../../lib/ui";
// La conversation. ⚠️ La validation vit CÔTÉ SERVEUR : ce module ne la
// redouble pas (deux vérités sur ce que l'opérateur a le droit de faire, ce
// serait deux vérités de trop).
import { demanderEnFlux } from "../../lib/conversation-api";
import FilEvenements from "./FilEvenements";
import {
  absorberLesEvenements,
  lireLaJournee,
  suivreLaJournee,
  traiterEvenement,
  rangerEvenement,
  rouvrirEvenement,
  refermerLaJournee,
  vueDuFil,
  heureDe,
  ageEnJours,
} from "../../lib/evenements-jour";
import { composerATraiter, tonDuPoids } from "../../lib/operateur-a-traiter";
import { estEnCarte, jourLocal } from "../../lib/evenements-poids";
import {
  lireFil,
  ajouterAuFil,
  suivreFil,
  viderFil,
  filSansDerniereReponse,
  gesteEnAttente,
  retirerGeste,
  expirationDuGeste,
  estDejaFait,
  DEJA_PREPARE,
  motifDuRetrait,
  confirmationEnCours,
} from "../../lib/fil-operateur";
// Ce qu'un clic a donné, mis en tour de fil — titre du REGISTRE, jamais les
// arguments. Voir `src/lib/geste-fait.js`.
import { composerFait, composerAnnulation } from "../../lib/geste-fait";
// « oui » / « non », lus par le code avant tout appel au modèle. Module PUR et
// volontairement étroit : le doute retombe toujours sur « ce n'est pas un
// assentiment » (`src/lib/operateur-acquiescement.js`).
import { lireAcquiescement } from "../../lib/operateur-acquiescement";
// L'empreinte du geste préparé — une chaîne opaque qui voyage avec le fil pour
// que le serveur ne repropose pas ce qui est déjà sur la table.
import { empreinteDeGeste } from "../../lib/geste-empreinte";
// La voix, dans les deux sens : il écoute (dictee) et il répond à voix haute
// (voix). Les deux SE TAISENT quand on quitte l'écran.
// LE MODE CONVERSATION : il écoute, il répond à voix haute, on peut le couper.
// La machine à états vit hors du composant — elle survit aux rendus, et elle se
// teste sans écran.
import { ouvrirConversation } from "../../lib/conversation-vocale";
import { taire } from "../../lib/voix";
import {
  refuseALaVoix,
  phraseDeuxCartes,
  avecMarqueur,
} from "../../lib/carte-dite";
import { chargerDonneesVue, oublierConsigne } from "../../lib/vues-donnees";
import { supabase } from "../../supabase";
// Les six dessins. Ils refusent de dessiner quand les données ne tiennent pas
// debout — une phrase le dit, jamais un dessin vide.
import DessinVue from "../operateur/DessinVue";
// Ce qu'il PRÉPARE, et que Dimitri clique. La carte porte toute la protection
// de la règle « il propose, je clique » : ce qui n'y est pas lisible n'est pas
// consenti.
import CarteGesteDemande from "../operateur/CarteGesteDemande";
// « Montre-moi l'ensemble avant de valider » — la feuille d'un formulaire en
// conversation. Elle ne calcule rien : le serveur compose la structure
// (`lib/formulaire-feuille.js`), ce composant la rend.
import FeuilleFormulaire from "../operateur/FeuilleFormulaire";
// La FEUILLE qu'un événement ouvre — « en prod tout ouvre un écran, chez moi
// ça ouvre une feuille ». Elle compose côté navigateur (miroir du serveur) et
// refuse d'afficher une description incomplète.
import FeuilleOperateur from "./FeuilleOperateur";

// ── L'HABILLAGE VIENT DE LA MAQUETTE (docs/handoff/admin-proto.html) ────────
// Version C (`.opBloc2` / `.opTete2` / `.opOrb` / `.opCorps` / `.opSai2`) :
// bloc blanc en dégradé sans bordure, tête transparente avec la sphère et un
// sous-titre, événements DANS le corps, saisie en carte blanche. Les valeurs
// sont celles du fichier (lues ligne à ligne le 2026-09-13), pas des
// approximations — Dimitri : « mon opérateur en ligne ne ressemble pas à ce
// qu'on a validé ».
//
// ⚠️ LA PALETTE EST CELLE DE L'ADMIN (`:root` de la maquette), pas la charte
// publique FBZ — voir `PALETTE_OP` dans src/lib/evenements-dessin.js.
import { PALETTE_OP } from "../../lib/evenements-dessin";
const BORD = PALETTE_OP.bord;

// ══ LA VANNE DU « OUI » VOCAL — FERMÉE PAR DÉFAUT ══════════════════════════
//
// ⚠️ CE LOT LIVRE LA LECTURE DE LA CARTE, PAS SON OUVERTURE. Le « oui » vocal
// reste refusé tant que Dimitri ne l'ouvre pas : la lecture est la CONDITION
// PRÉALABLE (« que je puisse valider à la voix une carte que je n'ai pas
// entendue, c'est un risque réel »), pas le geste lui-même.
//
// Lue à la compilation, comme tout `REACT_APP_*` sous CRA — ce n'est pas un
// réglage d'exécution, et ça ne doit pas pouvoir se lever d'un clic.
//
// ⚠️ ET INJECTABLE EN TEST (`_internal.OUI_VOCAL`). Sans ça, quatre mutations
// du cadrage ne PEUVENT PAS dire non : vanne fermée, le refus est le même
// avec et sans la garde de lecture — elles seraient vertes pour rien. Chaque
// garde a donc sa contre-épreuve positive, vanne ouverte.
const OUI_VOCAL_DEFAUT = process.env.REACT_APP_OPERATEUR_OUI_VOCAL === "true";

// La fenêtre du « oui » après la lecture. ⚠️ Elle se conjugue EN ET avec la
// péremption de dix minutes de la carte (`expirationDuGeste`) : la plus courte
// gagne, toujours.
const FENETRE_OUI_MS = 20000;

export const _internal = {
  OUI_VOCAL: OUI_VOCAL_DEFAUT,
  FENETRE_OUI_MS,
};

// Ce que la carte dit quand le serveur vient de retirer son sosie : elle est
// toujours là, et c'est exactement ce que Dimitri doit lire (revue adverse
// #1432, MAJEUR 3).
const RAPPEL_DEJA_PREPARE =
  "Ce geste est déjà prêt ci-dessous — dites « oui » ou cliquez.";
const FOND = PALETTE_OP.fond;
const CIEL = PALETTE_OP.ciel;
const CIEL_P = PALETTE_OP.cielP;
const NAVY = PALETTE_OP.navy;
const NAVY_D = PALETTE_OP.navyD;
const GRIS = PALETTE_OP.gris;
const GRIS_D = PALETTE_OP.grisD;
const VERT = PALETTE_OP.vert;
const TERRA = PALETTE_OP.terra;
const TERRA_TX = PALETTE_OP.terraTx;

// Ce qui ne s'écrit pas en style inline : pseudo-éléments (l'anneau de la
// sphère), survols, et la coupure `prefers-reduced-motion`. Une seule feuille,
// rendue une fois dans le bloc. Les règles sont celles de la maquette :
//   .opOrb / .opOrb.dort / .opOrb.veille / .opOrb.cherche / @keyframes opH
//   @keyframes opResp / .opDetL:hover / .opDetL .ok:hover / .opTete2 .n
const STYLE_OP = `.fbzOpOrb{border-radius:50%;flex-shrink:0;position:relative;display:flex;align-items:center;justify-content:center;color:#fff;transition:all .4s}
.fbzOpOrb-dort{background:radial-gradient(circle at 32% 28%,#d8e4ee,#b8c8d6 60%,#9aabb9);box-shadow:0 1px 4px rgba(35,50,64,.12)}
.fbzOpOrb-veille,.fbzOpOrb-cherche{background:radial-gradient(circle at 32% 28%,#a8cdf0,${CIEL} 46%,#5d95cc)}
.fbzOpOrb-veille{box-shadow:0 0 0 4px rgba(135,184,230,.16),0 3px 10px rgba(93,149,204,.32)}
.fbzOpOrb-veille::after,.fbzOpOrb-cherche::after{content:'';position:absolute;inset:-4px;border-radius:50%;border:1.5px solid rgba(135,184,230,.5)}
.fbzOpOrb-veille::after{animation:fbzOpH 2.8s ease-in-out infinite}
.fbzOpOrb-cherche::after{animation:fbzOpH .9s ease-in-out infinite}
.fbzOpOrb-cherche{animation:fbzOpResp 1.5s ease-in-out infinite}
@keyframes fbzOpH{0%,100%{transform:scale(1);opacity:.55}50%{transform:scale(1.18);opacity:0}}
@keyframes fbzOpResp{0%,100%{transform:scale(1)}50%{transform:scale(.94)}}
.fbzOpNu .fbzOpOrb{box-shadow:0 0 0 7px rgba(135,184,230,.12),0 6px 20px rgba(93,149,204,.3);transition:transform .2s cubic-bezier(.22,1,.36,1),box-shadow .2s}
.fbzOpNu .fbzOpOrb::after{inset:-7px;border-color:rgba(135,184,230,.4);animation:fbzOpH 3.4s ease-in-out infinite}
.fbzOpNu:hover .fbzOpOrb{transform:scale(1.06);box-shadow:0 0 0 10px rgba(135,184,230,.16),0 8px 26px rgba(93,149,204,.36)}
.fbzOpDetL{transition:background .13s}
.fbzOpDetL:hover{background:${FOND}}
.fbzOpDetOk:hover{background:#fff;color:${PALETTE_OP.vertTx}}
@media (prefers-reduced-motion:reduce){.fbzOpOrb,.fbzOpOrb::after{animation:none!important}}`;

// ⚠️ LE CERVEAU EST SON SYMBOLE DEPUIS LE 2026-09-09 — plus le micro. Le micro
// disait « dictée » ; c'est un interlocuteur, pas un dictaphone.
function Cerveau({ taille = 15 }) {
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9.5 3.5a2.5 2.5 0 0 0-2.4 1.8 2.4 2.4 0 0 0-2.1 2.4c0 .5.2 1 .4 1.4a2.4 2.4 0 0 0-.9 1.9c0 .8.4 1.5 1 1.9a2.4 2.4 0 0 0 .5 2.9 2.5 2.5 0 0 0 1.7 3.2 2.5 2.5 0 0 0 4.3 1.5V4.6a2.5 2.5 0 0 0-2.5-1.1z" />
      <path d="M14.5 3.5a2.5 2.5 0 0 1 2.4 1.8 2.4 2.4 0 0 1 2.1 2.4c0 .5-.2 1-.4 1.4a2.4 2.4 0 0 1 .9 1.9c0 .8-.4 1.5-1 1.9a2.4 2.4 0 0 1-.5 2.9 2.5 2.5 0 0 1-1.7 3.2 2.5 2.5 0 0 1-4.3 1.5V4.6a2.5 2.5 0 0 1 2.5-1.1z" />
      <path d="M9.7 7.6c-.9.3-1.3 1-1.2 1.8M9 12.4c-1 .1-1.6.7-1.7 1.6M14.3 7.6c.9.3 1.3 1 1.2 1.8M15 12.4c1 .1 1.6.7 1.7 1.6" />
    </svg>
  );
}

/** Les trois points — il réfléchit. Mieux qu'une attente muette. */
function TroisPoints() {
  return (
    <span
      data-testid="operateur-points"
      style={{
        display: "inline-flex",
        gap: 4,
        padding: "8px 11px",
        background: FOND,
        borderRadius: 12,
      }}
    >
      <style>{`@keyframes fbzOpB{0%,60%,100%{opacity:.25;transform:translateY(0)}30%{opacity:1;transform:translateY(-3px)}}`}</style>
      {[0, 0.2, 0.4].map((d) => (
        <i
          key={d}
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: GRIS_D,
            animation: `fbzOpB 1.3s ${d}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

/**
 * SA SPHÈRE (`.opOrb`, maquette) : 32px, dégradé radial, cerveau BLANC, un
 * anneau qui pulse en `veille` (2.8s) et plus vite en `cherche` (.9s), une
 * respiration en `cherche`. Grise et immobile quand il dort. La transition
 * entre états dure .4s. Le cas « rien d'ouvert » la rend en 64px (`.opNu .sph`).
 */
function Orbe({ etat, taille = 32, cerveau = 15 }) {
  return (
    <span
      data-testid="operateur-orbe"
      data-etat={etat}
      className={`fbzOpOrb fbzOpOrb-${etat}`}
      style={{ width: taille, height: taille }}
    >
      <Cerveau taille={cerveau} />
    </span>
  );
}

/** L'avatar des tours du fil : cerveau blanc dans un rond ciel. */
function Pastille({ taille = 25, fond = "#fff", encre = NAVY_D }) {
  return (
    <span
      style={{
        width: taille,
        height: taille,
        borderRadius: "50%",
        background: fond,
        color: encre,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Cerveau taille={Math.round(taille * 0.6)} />
    </span>
  );
}

export default function BlocOperateur({
  nbPropositions,
  // La table des noms : les dessins ne parlent pas en codes non plus.
  noms,
  motifDormant,
  onOuvrir,
  ecran,
  // ⚠️ CE QUE L'ÉCRAN AFFICHE, tel qu'il l'affiche (règle générale Dimitri
  // 2026-09-12 : « il doit voir la même ligne, pas la sienne »). L'accueil le
  // compose avec `composerReleveAccueil` à partir des lignes qu'il vient de
  // peindre — et c'est cette déclaration, pas une seconde requête, qui fait
  // foi côté serveur sur ses trois listes.
  accueil,
  // ── CE QU'IL Y A À FAIRE, EN SEPT LIGNES ───────────────────────────────
  // Dimitri, 2026-09-13 : « Je clique dessus : une liste compacte se déplie.
  // Sept lignes, chacune avec son point de couleur et son VRAI geste — pas un
  // résumé. Les coches "Rien à répondre" y sont. »
  //
  // ⚠️ CES LIGNES VIENNENT DE L'ÉCRAN, comme le reste (règle du 2026-09-12) :
  // l'accueil les compose à partir de ce qu'il vient de peindre. Le bloc ne
  // requête rien et ne recompte rien — il rend.
  aFaire,
  // Ce qu'il annonce au fil de l'eau, déjà trié par l'accueil.
  evenements,
  // Ce que le serveur a coupé (des traits, jamais une carte) : le panneau
  // « il remarque » le dit, plutôt que de laisser croire qu'il n'y a rien.
  coupes = 0,
  // ⚠️ UNE LECTURE EN PANNE SE DIT. Sans ça, « les annonces n'ont pas pu être
  // lues » et « rien qui presse » ont exactement la même tête — et Dimitri
  // conclut au calme sur un écran aveugle.
  annoncesMotif = null,
  // « Montrer tout ce qu'il y a à faire › » — les cartes entières reviennent.
  onVoirTout,
  cartesOuvertes,
  onAller,
}) {
  const [brouillon, setBrouillon] = useState("");
  const [dit, setDit] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [reponse, setReponse] = useState(null);
  // La prose qui arrive au fil de l'eau, AVANT la réponse validée. Les deux ne
  // se mélangent pas : celle-ci s'efface quand celle-là arrive.
  const [enTrain, setEnTrain] = useState("");
  // Où il en est : « faits » tant qu'il rassemble, « ecrit » dès que le
  // serveur a ouvert le flux. Deux mots, mais ils changent l'attente en
  // patience.
  // ⚠️ ZÉRO BLANC (Dimitri, 2026-09-09) : « cinq secondes où rien ne se passe,
  // c'est insupportable ». `etape` porte la PHRASE que le serveur envoie au fur
  // et à mesure — « je lis tes messages… », « je compare avec le mois
  // dernier… » — et chacune correspond à une source qui vient réellement de
  // répondre.
  const [etape, setEtape] = useState("je regarde…");
  // Le détail de son état, replié par défaut : sept lignes d'affilée ne se
  // lisent pas à l'ouverture d'un écran.
  const [detOuvert, setDetOuvert] = useState(false);
  // Le geste d'un événement, DÉPLIÉ SOUS LUI — jamais exécuté au clic.
  // ⚠️ Le lien-bouton d'un événement ne fait pas partir un geste : il ouvre la
  // carte qui dit ce qui va se passer, et c'est le clic de Dimitri, avec SON
  // identité, qui l'exécute. L'invariant du chantier tient ici aussi.
  const [gesteEvenement, setGesteEvenement] = useState(null);
  // Le panneau « il remarque », replié par défaut : ce sont des constats, ils
  // attendent qu'on vienne les lire.
  const [remarquesOuvertes, setRemarquesOuvertes] = useState(false);
  // « Votre journée » : tout, y compris ce qui a été traité.
  const [journeeOuverte, setJourneeOuverte] = useState(false);
  // Le geste déplié SOUS une ligne de la feuille « Ce qui reste à faire ».
  // ⚠️ LA FEUILLE NE SE FERME PAS POUR AGIR (Dimitri, 2026-09-15) : « si je
  // clique sur une action et que je perds ma liste, je ne reviens pas la
  // finir ». La carte se déplie EN PLACE, la ligne passe traitée, le compte
  // décroît, et on enchaîne la suivante sans quitter l'écran.
  const [gesteDeLaListe, setGesteDeLaListe] = useState(null);

  // ── LA JOURNÉE, QUI NE SE PERD PAS ─────────────────────────────────────
  // Les annonces arrivent par lots (le curseur ne rend que le nouveau) et
  // s'ACCUMULENT ici (src/lib/evenements-jour.js). Sans ça, une ligne née à
  // 9 h 02 disparaissait au rafraîchissement de 9 h 05 sans que personne
  // l'ait vue — « rangé » ne doit jamais vouloir dire « jeté ».
  // ⚠️ QUITTER LA LISTE NE DOIT PAS LA PERDRE (Dimitri, 2026-09-15) : « si je
  // clique sur une action et que je perds ma liste, je ne reviens pas la
  // finir. » Un geste se fait SANS fermer la feuille ; seul un déplacement
  // d'écran la referme — et elle se rouvre au retour, à la même place.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem("fbz-a-traiter-ouvert") === "1") {
        window.sessionStorage.removeItem("fbz-a-traiter-ouvert");
        setDetOuvert(true);
      }
    } catch (e) {
      /* sans mémoire de session, la liste se rouvre à la main */
    }
  }, []);

  // ⚠️ LE JOUR DE PARIS, PAS CELUI DE GREENWICH (m8) : entre minuit et 2 h,
  // les deux ne sont pas le même, et « demain » devenait « dans deux jours ».
  // ⚠️ Et il se calcule ICI, avant les effets qui en dépendent : une `const`
  // lue dans un tableau de dépendances avant sa déclaration lève au rendu.
  const jour = jourLocal();
  const [journee, setJournee] = useState(lireLaJournee);
  useEffect(() => suivreLaJournee(setJournee), []);
  useEffect(() => {
    if (Array.isArray(evenements) && evenements.length)
      absorberLesEvenements(evenements);
  }, [evenements]);
  // ⚠️ REFERMER LA JOURNÉE EST UNE ÉCRITURE : dans le corps du rendu, elle
  // prévenait ses abonnés PENDANT que React rendait — un `setState` en plein
  // rendu, que React finit par punir. C'est un effet.
  useEffect(() => {
    refermerLaJournee(jour);
  }, [jour]);
  // L'événement dont la feuille est ouverte (sa ligne a quitté le fil).
  const [feuilleEvenement, setFeuilleEvenement] = useState(null);
  // Le fil vit dans un store PARTAGÉ (src/lib/fil-operateur.js) : la barre des
  // autres écrans écrit dans le même. Un état local mourrait au changement
  // d'écran — or c'est précisément là qu'on demande « et pour l'autre ? ».
  const [fil, setFil] = useState(lireFil);
  useEffect(() => suivreFil(setFil), []);
  // ── LA CARTE SURVIT À LA QUESTION SUIVANTE (2026-09-15) ──────────────────
  // ⚠️ Elle vivait dans `reponse.geste`, et `envoyerQuestion` commence par
  // `setReponse(null)` : la carte était donc DÉTRUITE à l'instant même où le
  // « oui » partait — c'est-à-dire exactement quand on en a besoin.
  // Elle a maintenant son état propre, et se retrouve aussi après un
  // rechargement de page, depuis le fil (`gesteEnAttente`).
  const [carte, setCarte] = useState(null);
  // Un simple compteur : il ne dit rien, il sert à se re-rendre quand la
  // fenêtre des dix minutes se ferme (voir « QUAND LE "OUI" CESSE DE PORTER »).
  // Sans lui, l'écran resterait figé sur un état qui n'est plus.
  const [, setTicTac] = useState(0);
  const carteRef = useRef(null);
  // ⚠️ LA CARTE D'ÉVÉNEMENT A SA PROPRE PRISE (revue adverse #1432, MAJEUR 4).
  // Elle ne naît pas d'une réponse : son geste n'entre donc pas dans le fil, et
  // un « oui » face à elle répondait « je n'ai rien en attente ». Pire, quand
  // les DEUX cartes étaient à l'écran, le « oui » lançait celle de la réponse
  // en silence — Dimitri regardait l'autre.
  const carteEvenementRef = useRef(null);
  // Ce que le fil MONTRE : tout, moins la réponse rendue en entier dessous
  // (avec sa vue et sa porte) — sinon il se répéterait.
  const toursAffiches = reponse ? filSansDerniereReponse(fil) : fil;
  // La carte à l'écran : celle de la dernière réponse, ou — après un
  // rechargement de page, où l'état local est neuf mais le fil persiste —
  // celle que le fil garde en attente.
  const gesteDuFil = gesteEnAttente();
  const carteAffichee = carte || (gesteDuFil ? { geste: gesteDuFil } : null);

  // ── QUAND LE « OUI » CESSE DE PORTER ────────────────────────────────────
  // ⚠️ `gesteEnAttente` se lit AU RENDU ; rien ne re-rend l'écran quand les dix
  // minutes tombent (revue adverse #1432, mineur 7). La carte resterait donc
  // identique pendant qu'un « oui » répondrait « je n'ai rien en attente » :
  // deux vérités à l'écran en même temps. On se re-rend à l'échéance, et on le
  // DIT sous la carte — le clic, lui, marche toujours.
  const expiration = expirationDuGeste();
  useEffect(() => {
    if (!expiration) return undefined;
    const reste = expiration - Date.now();
    if (reste <= 0) return undefined;
    const t = setTimeout(() => setTicTac((n) => n + 1), reste + 50);
    return () => clearTimeout(t);
  }, [expiration]);
  const gestePerime = !!carteAffichee && !!expiration && !gesteDuFil;

  // ── LES CARTES QU'UN « OUI » POURRAIT LANCER ────────────────────────────
  // ⚠️ Celle de la RÉPONSE ne compte que si le fil la dit encore en attente :
  // une carte déjà exécutée (un compte rendu la suit) ou périmée reste à
  // l'écran — elle porte son « ✓ C'est fait » — mais « oui » n'a plus à la
  // relancer.
  //
  // ⚠️ ET CELLE D'UN ÉVÉNEMENT NON PLUS, UNE FOIS SERVIE (revue #1432, seconde
  // passe). Elle n'est attachée à aucun tour « lui » : rien ne disait donc
  // qu'elle avait déjà servi, et un second « oui » la relançait — exactement ce
  // que le compte rendu ferme du côté de la réponse. Son empreinte, elle, est
  // dans le fil dès qu'elle a été cliquée : c'est elle qu'on interroge.
  function cartesPretes() {
    const out = [];
    if (carteAffichee && gesteDuFil)
      // ⚠️ LE GESTE VOYAGE AVEC LA PRISE (B2). Sans lui, le guichet vocal ne
      // pourrait ni composer l'empreinte de CE QUI SERAIT LANCÉ, ni consulter
      // ses marquages — et la garde argent porterait dans le vide.
      out.push({ quoi: "reponse", ref: carteRef, geste: gesteDuFil });
    if (
      gesteEvenement &&
      gesteEvenement.geste &&
      !estDejaFait(empreinteDeGeste(gesteEvenement.geste))
    )
      out.push({
        quoi: "evenement",
        ref: carteEvenementRef,
        geste: gesteEvenement.geste,
      });
    return out;
  }

  // ── LE BANDEAU DU HAUT A ÉTÉ RETIRÉ (2026-09-15) ───────────────────────
  //
  // Il portait la phrase de journée, puis le brief du matin quand il arrivait :
  // « 4 arrivées et 2 départs aujourd'hui. 2 ménages sans agent. 26 points à
  // régler. 1 point sur les cautions. Soleil du Sud est au-dessus de son
  // habitude (+41 points) sur 2026-08. »
  //
  // Dimitri, 2026-09-15 : « Le bandeau du haut ne sert à rien. Au-dessus de la
  // conversation, ça ne sert à rien. Retire-le. » Et le défaut d'à côté : ses
  // « 26 points à régler » ne correspondaient à rien de ce que le fil montrait
  // — deux comptes à l'écran, et aucun moyen de savoir lequel disait vrai.
  //
  // ⚠️ CE QUI RESTE, ET CE QUI PART. Le moteur serveur (`operateur_brief`,
  // `lib/operateur-brief.js`) et `src/lib/operateur-journee.js` sont INTACTS :
  // ils servent ailleurs et resserviront si Dimitri veut de nouveau un mot
  // d'ouverture. C'est l'APPEL depuis cet écran qui part avec le bandeau —
  // garder une requête dont plus personne ne lit la réponse serait payer un
  // appel au modèle pour rien.

  // ── LE MODE CONVERSATION ───────────────────────────────────────────────
  // Dimitri, 2026-09-09 : « je ne veux pas qu'il pré-remplisse mes questions,
  // je veux qu'on discute ». Une fois ouvert, les questions partent seules ;
  // ⚠️ mais RIEN QUI AGISSE ne part à la voix — et l'écran ne se déplace pas
  // non plus tout seul (voir `sollicite` plus bas) : une phrase mal entendue
  // ne doit rien changer.
  //
  // ⚠️ ET DEPUIS QUE « OUI » VAUT LE CLIC, CETTE RÈGLE S'ÉTEND À
  // L'ASSENTIMENT (revue adverse #1432, MAJEUR 1). Le « oui » n'est plus une
  // question comme les autres : c'est un déclencheur. Un « ouais » capté par
  // le micro — dans une conversation avec quelqu'un d'autre, une phrase mal
  // entendue — lancerait un geste d'argent. La branche d'assentiment consulte
  // donc `enVocalRef.current` et refuse, en le disant.
  const [vocal, setVocal] = useState(null); // 'ecoute' | 'envoi' | 'parle' | null
  const [entendu, setEntendu] = useState("");
  const [avis, setAvis] = useState(null);
  const session = useRef(null);
  const enVocal = vocal !== null && vocal !== "ferme";
  // ⚠️ UNE RÉFÉRENCE, PAS L'ÉTAT — piège de la fermeture périmée (CLAUDE.md).
  // `envoyerQuestion` capture `enVocal` au rendu où elle est définie. Une
  // question posée à la voix AVANT que React n'ait re-rendu lirait donc
  // `false`, et l'écran se déplacerait — exactement ce que la réserve de
  // Dimitri interdit. Un test l'a attrapé : « rien ne déplace l'écran à la
  // voix » échouait, et c'était le CODE qui avait tort.
  // Elle est posée À L'OUVERTURE, pas au rendu suivant : le mode est ouvert
  // dès qu'on l'ouvre, quel que soit le calendrier de React.
  const enVocalRef = useRef(false);
  // ⚠️ MÊME PIÈGE, AUTRE CHEMIN (B2). `ouvrirConversation` est appelée UNE
  // fois, au clic sur « parler » : elle capturerait `cartesPretes` du rendu de
  // ce clic-là, et lirait à voix haute la carte d'il y a trois questions. On
  // repose donc la fonction du rendu courant à CHAQUE rendu.
  const cartesPretesRef = useRef(() => []);
  // ⚠️ ET LE CHEMIN D'ENVOI LUI-MÊME (trouvé par un test de ce lot, 2026-09-22).
  // `ouvrirConversation` est appelée UNE fois, au clic sur « parler » : le
  // `poser` qu'elle reçoit capture l'`envoyerQuestion` de CE rendu-là, et donc
  // le `cartesPretes` de ce rendu-là. Tout le reste de la conversation — dix
  // minutes, vingt questions — rendait ses verdicts sur l'écran de la première
  // seconde. Le symptôme : une carte périmée depuis onze minutes se lançait
  // encore, pendant que le guichet, lui, disait justement qu'il n'y avait plus
  // rien à lire. Deux vérités en même temps, et la fail-OPEN du côté qui agit.
  const envoyerQuestionRef = useRef(null);
  // La dernière prose dite — pour que la carte ne répète pas ce qu'il vient
  // d'entendre (règle Dimitri : « jamais deux fois le même texte »).
  const proseRef = useRef("");
  // ══ LE GENRE DE L'AVERTISSEMENT QUI PRÉCÈDE LA CARTE ═════════════════════
  //
  // ⚠️ LA PHRASE, ELLE, EST DÉJÀ DANS LA PROSE (le serveur la pose en tête de
  // `reponse.texte`) : elle est donc DITE, en entier, avant l'énoncé 1. On ne
  // la redit pas — on garde son GENRE pour rappeler l'alerte en vingt
  // caractères au moment où le chiffre se dit, juste avant le « oui ».
  const avantTypeRef = useRef(null);
  // Ce que le dernier geste a donné, pour l'ANNONCER après coup : un « oui »
  // lancé dans la pièce est irrattrapable, il doit au moins s'entendre.
  const dernierFaitRef = useRef("");

  // ⚠️ SANS LISTE DE DÉPENDANCES : à chaque rendu. C'est ce qui garantit que
  // le guichet vocal interroge l'écran d'AUJOURD'HUI.
  useEffect(() => {
    cartesPretesRef.current = cartesPretes;
    envoyerQuestionRef.current = envoyerQuestion;
  });

  // ⚠️ Le micro et la voix se coupent quand on quitte l'écran. Une voix qui
  // continue après qu'on est parti est pire que pas de voix.
  useEffect(
    () => () => {
      taire();
      if (session.current) session.current.fermer();
    },
    [],
  );

  function basculerVocal() {
    if (session.current) {
      session.current.fermer();
      session.current = null;
      enVocalRef.current = false;
      setVocal(null);
      setEntendu("");
      return;
    }
    setAvis(null);
    enVocalRef.current = true;
    session.current = ouvrirConversation({
      // ⚠️ `poser` ENVOIE UNE QUESTION, et rien de ce chemin ne peut agir — mais
      // ce n'est plus vrai « par construction » depuis que « oui » vaut le
      // clic : c'est la garde `enVocalRef.current` en tête de la branche
      // d'assentiment qui le tient. Ne jamais la retirer sans décision de
      // Dimitri (revue adverse #1432, MAJEUR 1).
      poser: (q, lu) => envoyerQuestionRef.current(q, lu),
      // ══ CE QU'IL Y A À DIRE DE LA CARTE ═══════════════════════════════
      //
      // ⚠️ ON NE LIT JAMAIS UNE CARTE QUE « OUI » NE LANCERAIT PAS. Le
      // guichet part de `cartesPretes()`, jamais de `carteRef` : une carte de
      // réponse PÉRIMÉE reste montée à l'écran (avec son bandeau « oui ne la
      // lance plus ») pendant qu'une carte d'ÉVÉNEMENT, elle, est lançable.
      // Lire l'une et lancer l'autre rouvrirait, par un autre chemin, le
      // défaut fermé par #1432.
      carteADire: (prose) => {
        const p = cartesPretesRef.current();
        if (!p.length) return { etat: "absente" };
        const phrases = p.map((x) =>
          x.ref.current && typeof x.ref.current.phrase === "function"
            ? x.ref.current.phrase({
                prose: prose === undefined ? proseRef.current : prose,
                vanneOuverte: _internal.OUI_VOCAL,
              })
            : { etat: "absente" },
        );
        if (p.length > 1)
          return {
            etat: "plusieurs",
            texte: phraseDeuxCartes(
              p.map((x, i) => ({
                titre: x.geste && x.geste.titre,
                nomLogement: phrases[i].nomLogement,
              })),
            ),
          };
        const seule = {
          ...phrases[0],
          empreinte: p[0].geste ? empreinteDeGeste(p[0].geste) : null,
        };
        // ⚠️ LE MARQUEUR NE SUIT QUE LA CARTE DE LA RÉPONSE. L'avertissement
        // porte sur les nuits du geste que le serveur vient de préparer ; le
        // coller sur une carte d'ÉVÉNEMENT — un autre logement, d'autres
        // dates — annoncerait une nuit vendue qui n'existe pas.
        return p[0].quoi === "reponse"
          ? avecMarqueur(
              seule,
              avantTypeRef.current,
              p[0].geste && p[0].geste.titre,
            )
          : seule;
      },
      onEtat: (e) => {
        enVocalRef.current = e !== "ferme";
        setVocal(e === "ferme" ? null : e);
      },
      onEntendu: setEntendu,
      onAvis: setAvis,
    });
  }
  // Les chiffres du dessin arrivent APRÈS la phrase, et séparément — le texte
  // ne doit pas attendre une lecture de base pour s'afficher.
  const [vueDonnees, setVueDonnees] = useState(null);

  // ── CE QU'UN CLIC A DONNÉ ────────────────────────────────────────────────
  //
  // Dimitri, 2026-09-15 : « il doit pouvoir me dire "c'est fait" en ayant LU la
  // réponse. » La carte affichait son « ✓ C'est fait » et ça s'arrêtait là :
  // au tour suivant, l'opérateur ne savait RIEN de ce que le clic avait donné,
  // et pouvait reproposer le geste qu'il venait de faire faire.
  //
  // ⚠️ C'EST LE CODE QUI ÉCRIT CE TOUR, jamais le modèle : le titre vient du
  // registre, l'issue du retour d'`executerGeste`, et les ARGUMENTS n'y entrent
  // pas (voir `src/lib/geste-fait.js`). Un levier inconnu ne produit aucun
  // tour — mieux vaut un silence qu'une ligne « fait » sans objet nommable.
  function noterFait(geste, r) {
    const tour = composerFait(geste, r);
    if (!tour) return;
    dernierFaitRef.current = tour.texte;
    ajouterAuFil("fait", tour.texte, { fait: tour.fait });
  }

  // ── PARLER ────────────────────────────────────────────────────────────────
  // ⚠️ LE TEXTE N'EST JAMAIS AVALÉ. On ne vide le champ qu'APRÈS une réponse
  // reçue — c'était déjà la doctrine quand la conversation était fermée, et
  // elle vaut d'autant plus maintenant qu'il y a un aller-retour réseau.
  // ⚠️ UN SEUL CHEMIN D'ENVOI, POUR LES DEUX ENTRÉES. Le clavier et la voix
  // envoient la MÊME question par la MÊME fonction. Deux chemins parallèles
  // divergeraient au premier correctif appliqué d'un seul côté — et la
  // divergence se verrait mal : les deux continueraient de répondre.
  // Une phrase déterministe, sans aucun appel au modèle. Elle entre dans le
  // fil comme une vraie réponse : un « oui » qui ne produit RIEN ni à l'écran
  // ni dans le fil est indiscernable d'une panne.
  // ⚠️ `neutre` : ce tour ne change pas ce qui est en attente (voir
  // `_tourDuGeste` dans `src/lib/fil-operateur.js`). Toutes les phrases du
  // chemin « oui » le sont — la carte est toujours à l'écran quand elles
  // s'affichent, et fermer l'attente ici demanderait à Dimitri d'écrire un
  // motif pour rien.
  function repondreSansModele(q, texte, neutre = true) {
    ajouterAuFil("moi", q);
    const r = { registre: "repondre", texte };
    setReponse(r);
    ajouterAuFil("lui", texte, neutre ? { neutre: true } : undefined);
    return r;
  }

  async function envoyerQuestion(q, lu) {
    // ── « OUI » VAUT LE CLIC (décision Dimitri, 2026-09-15) ────────────────
    //
    //   « Le "oui" doit devenir le clic […] qu'il aille au bout du geste quand
    //     je dis oui. »
    //
    // ⚠️ AUCUN POUVOIR NOUVEAU : on n'exécute pas ici, on APPUIE SUR LE BOUTON
    // de la carte (`carteRef.current.lancer()`), qui garde ses gardes — motif,
    // choix, aperçu, vague — et passe par `executerGeste` avec le jeton de
    // Dimitri. Et aucun appel au modèle : un « oui » n'a rien à faire
    // reformuler.
    const { verdict } = lireAcquiescement(q);
    // ── LE « OUI » D'UNE CONFIRMATION DE NOM VA AU SERVEUR ──────────────
    //
    // ⚠️ VU EN PROD LE 22/09, APRÈS #1489 ET #1498. « Désactive le gabarit
    // Rappel J-2 » ⇒ « je comprends “Rappel J-2 (caution / formulaire) —
    // voyageur” : c'est bien celui-là ? » — puis « oui » ⇒ « À quoi voulez-vous
    // que je dise oui ? Je n'ai rien en attente ». Tout le chemin serveur
    // existait (`confirmation_attendue` posé, rangé dans le fil, renvoyé par
    // `conversation-api`, lu par `lib/confirmation-nom.js` AVANT le modèle) et
    // il n'était jamais atteint : l'assentiment était intercepté ICI, qui ne
    // regardait que les CARTES prêtes — et une question de confirmation n'a,
    // par construction, aucune carte. Le « oui » n'atteignait pas le serveur.
    //
    // Une confirmation en attente est la question la plus récente de
    // l'écran : son « oui » (et son « non ») lui reviennent, et c'est le
    // SERVEUR qui les lit — sans un jeton, en recomposant la carte sur le nom
    // exact. Rien n'est lancé ici : la carte n'existe pas encore, donc la
    // réserve « ce qui agit ne part jamais à la voix » ne s'applique pas —
    // répondre « oui » à « c'est bien celui-là ? » prépare, il n'exécute pas.
    //
    // ⚠️ SEULEMENT S'IL N'Y A RIEN À CLIQUER (revue adverse, majeur 1). Une
    // carte d'ÉVÉNEMENT n'est attachée à aucun tour « lui » : Dimitri peut la
    // déplier PENDANT qu'une confirmation attend, et son « oui » la vise,
    // elle — la déférer au serveur aurait recomposé l'AUTRE geste en silence,
    // et « non » n'aurait plus retiré la carte. La confirmation n'a jamais de
    // carte : « rien de prêt » est exactement le cas du défaut de prod.
    const confirmationDeNom =
      verdict && !cartesPretes().length ? confirmationEnCours() : null;
    if (verdict === "lance" && !confirmationDeNom) {
      // ── 1 · JAMAIS À LA VOIX (revue adverse #1432, MAJEUR 1) ─────────────
      // « Ce qui agit ne doit jamais partir à la voix » (Dimitri, 2026-09-09).
      // L'assentiment AGIT désormais : il tombe sous la même réserve que le
      // déplacement d'écran. La carte, elle, reste prête — le refus n'est pas
      // une perte, c'est un clic de plus.
      const pretes = cartesPretes();
      if (enVocalRef.current) {
        const geste1 = pretes.length === 1 ? pretes[0].geste || null : null;
        // ── 1a · CHEMIN ARGENT — UNE CONDITION À PART, JAMAIS LA VANNE ─────
        //
        // ⚠️ IL N'EXISTE AUCUNE RÉTRACTATION APRÈS UN LANCEMENT VOCAL. Le
        // « non » ne retire qu'une carte NON LANCÉE (voir la branche
        // `annule` plus bas) ; une fois le geste parti, rien ne le rappelle.
        // Cette garde vaut VANNE OUVERTE COMME FERMÉE : la fondre dans la
        // vanne, c'est exactement la manière dont l'argent finit par passer —
        // le jour où l'on ouvre, on emporte tout. Elle est donc AVANT, et elle
        // a sa contre-épreuve vanne fermée.
        //
        // ⚠️ ET ELLE SE DÉCIDE AU REGISTRE, PAS SUR LE GESTE SERVI. Une carte
        // d'ÉVÉNEMENT arrive en `{levier, args}` nu : `geste.marquages` y vaut
        // `undefined`, et la garde était un NO-OP sur tout le fil d'événements
        // — `pricing_apply_base` pousse un prix chez Beds24. Voir
        // `refuseALaVoix` (fail-closed sur levier inconnu).
        const verdictVoix = refuseALaVoix(geste1 || { levier: null });
        if (geste1 && verdictVoix.refuse)
          return repondreSansModele(
            q,
            // Le motif est un complément sans sujet (« ne se rattrape pas »,
            // « porte un montant ») : il se lit derrière « il ».
            `Ce geste-là ne part pas à la voix : il ${verdictVoix.motif}. La carte est prête : cliquez-la.`,
          );
        // ── 1b · LA VANNE ──────────────────────────────────────────────────
        if (!_internal.OUI_VOCAL)
          return repondreSansModele(
            q,
            "À la voix je ne lance rien — la carte est prête, cliquez-la.",
          );
        if (!pretes.length)
          return repondreSansModele(
            q,
            "À quoi voulez-vous que je dise oui ? Je n'ai rien en attente — redemandez-moi le geste, je le prépare.",
          );
        if (pretes.length > 1)
          return repondreSansModele(
            q,
            "Deux gestes sont prêts : cliquez celui que vous voulez.",
          );
        // ── 1c · IL FAUT L'AVOIR ENTENDUE, ET C'EST BIEN CELLE-LÀ ──────────
        //
        // ⚠️ UNE EMPREINTE, PAS UN DRAPEAU. Une carte d'ÉVÉNEMENT peut
        // apparaître entre la lecture et le « oui » sans repasser par une
        // question : un « j'ai lu quelque chose » lui ferait hériter du « lu »
        // d'une AUTRE carte. On compare l'identité de ce qui serait lancé.
        const attendue = geste1 ? empreinteDeGeste(geste1) : null;
        const entendue = (lu && lu.carteDiteEmpreinte) || null;
        if (!attendue || entendue !== attendue)
          return repondreSansModele(
            q,
            "Je ne vous l'ai pas lue en entier — redemandez-la-moi, je la relis avant que vous décidiez.",
          );
        // ── 1d · DEUX HORLOGES, EN ET ──────────────────────────────────────
        // La fenêtre de vingt secondes ET la péremption de dix minutes (celle
        // -ci déjà tenue par `cartesPretes`, qui ne rend plus la carte au-delà).
        // La plus courte gagne.
        if (
          Date.now() - ((lu && lu.carteDiteA) || 0) >
          _internal.FENETRE_OUI_MS
        )
          return repondreSansModele(
            q,
            "Je vous l'ai lue il y a un moment déjà — redemandez-la-moi, je la relis avant que vous décidiez.",
          );
      }
      // ── 2 · UNE SEULE CARTE, SINON ON NE DEVINE PAS ──────────────────────
      // Deux cartes à l'écran (une réponse ET un événement déplié) : « oui »
      // n'a pas de destinataire évident, et en choisir un lancerait en silence
      // celui que Dimitri ne regardait pas.
      if (!pretes.length)
        return repondreSansModele(
          q,
          "À quoi voulez-vous que je dise oui ? Je n'ai rien en attente — redemandez-moi le geste, je le prépare.",
        );
      if (pretes.length > 1)
        return repondreSansModele(
          q,
          "Deux gestes sont prêts : cliquez celui que vous voulez.",
        );
      setDit(false);
      setEnTrain("");
      setReponse(null);
      ajouterAuFil("moi", q);
      taire();
      // ⚠️ VIDÉ AVANT, PAS APRÈS. `composerFait` rend `null` sur un levier que
      // le registre ne connaît pas : sans cette remise à zéro, l'annonce
      // d'après-coup annoncerait LE GESTE PRÉCÉDENT — « c'est fait » sur autre
      // chose que ce qui vient d'être fait.
      dernierFaitRef.current = "";
      const prise = pretes[0].ref.current;
      const lancement = prise
        ? await prise.lancer()
        : { lance: false, raison: "La carte n'est pas prête — cliquez-la." };
      if (lancement && lancement.lance)
        // ⚠️ UN GESTE LANCÉ À LA VOIX S'ANNONCE, NOMMÉ (T6). À l'écran, la
        // carte porte son « ✓ C'est fait » et ça suffit. À la voix, il n'a que
        // sa parole : un « oui » qui n'était pas pour lui — capté dans la
        // pièce — doit au moins s'entendre. Le texte vient de `composerFait`
        // (le registre, jamais le modèle), posé par `noterFait`.
        return {
          registre: "repondre",
          texte: enVocalRef.current ? dernierFaitRef.current || "" : "",
        };
      // ⚠️ IL DEMANDE, IL N'EXÉCUTE PAS À MOITIÉ. Un geste incomplet (motif
      // manquant, choix non fait, aperçu non lu) ne part pas sur un « oui » :
      // la carte reste, et il DIT ce qui manque.
      const raison =
        (lancement && lancement.raison) ||
        "Ce geste ne peut pas partir en l'état — regardez la carte.";
      setReponse({ registre: "repondre", texte: raison });
      // Neutre : la carte reste, et c'est elle qu'on va compléter.
      ajouterAuFil("lui", raison, { neutre: true });
      return { registre: "repondre", texte: raison };
    }
    if (verdict === "annule" && !confirmationDeNom) {
      // ⚠️ MÊMES GARDES QUE « OUI » (revue #1432, seconde passe). Annuler
      // n'envoie rien, mais ça DÉTRUIT quelque chose : un « non » ou un
      // « stop » mal entendu ferait disparaître une carte que Dimitri venait
      // de faire préparer, et il faudrait la redemander sans savoir pourquoi
      // elle s'est évaporée. La symétrie n'est donc pas une élégance : les
      // deux verdicts agissent, les deux passent par les mêmes conditions.
      const aRetirer = cartesPretes();
      // Rien de prêt : ce n'est pas une annulation, c'est une phrase. Elle
      // repart au modèle, comme avant.
      if (aRetirer.length) {
        if (enVocalRef.current)
          return repondreSansModele(
            q,
            "À la voix je n'annule rien — cliquez la croix de la carte.",
          );
        if (aRetirer.length > 1)
          return repondreSansModele(
            q,
            "Deux gestes sont prêts : écartez celui que vous voulez.",
          );
        // ⚠️ L'ORDRE EST CELUI DE LA CONVERSATION : son « non » d'abord, le
        // compte rendu d'annulation ensuite, sa réponse enfin. Poser
        // l'annulation avant le « non » daterait le refus avant la phrase qui
        // l'a causé — et le fil se relirait à l'envers.
        setDit(false);
        setEnTrain("");
        setReponse(null);
        ajouterAuFil("moi", q);
        taire();
        if (aRetirer[0].quoi === "evenement") {
          // Une carte d'événement n'a pas de tour à elle : on pose quand même
          // son compte rendu, pour qu'elle ne soit pas reproposée comme si de
          // rien n'était.
          const tour = composerAnnulation(
            aRetirer[0].geste,
            "vous avez dit non",
          );
          if (tour) ajouterAuFil("fait", tour.texte, { fait: tour.fait });
          setGesteEvenement(null);
        } else {
          // On ne supprime pas le tour qui portait la carte : on pose un compte
          // rendu « annulé ». La conversation continue, elle ne se réécrit pas.
          retirerGeste("vous avez dit non");
          setCarte(null);
        }
        const laisse = "Entendu, je laisse tomber. Rien n'a été fait.";
        setReponse({ registre: "repondre", texte: laisse });
        ajouterAuFil("lui", laisse);
        return { registre: "repondre", texte: laisse };
      }
    }

    setDit(false);
    setEnTrain("");
    setReponse(null);
    // ⚠️ La question entre dans le fil AVANT de partir : si la réponse
    // n'arrive jamais, on doit quand même voir ce qu'on a demandé.
    ajouterAuFil("moi", q);
    // Une nouvelle question coupe la lecture de la précédente : deux voix
    // superposées sont incompréhensibles.
    taire();
    setEtape("je regarde…");
    const r = await demanderEnFlux(
      q,
      ecran || accueil ? { ecran, accueil: accueil || null } : null,
      setEnTrain,
      setEtape,
    );
    setEnTrain("");
    setReponse(r);
    // Ce qu'il vient de dire : la carte ne le redira pas (règle de rôle).
    proseRef.current = r.texte || "";
    // ⚠️ LE MARQUEUR SUIT LA CARTE, PAS LE TOUR. Quand le serveur répond
    // « déjà préparé » sans geste, la carte à l'écran est celle d'AVANT : son
    // avertissement aussi. L'effacer ici retirerait « Nuit déjà vendue — » de
    // l'énoncé 1 d'une carte qui, elle, n'a pas bougé.
    if (r.geste || motifDuRetrait(r) !== DEJA_PREPARE)
      avantTypeRef.current = r.avant_carte_type || null;
    // C'est la réponse VALIDÉE qui entre au fil, jamais la prose qui défilait.
    // La trace de ce qu'il a lu voyage avec le tour : le serveur la relit
    // quand Dimitri demande « qu'est-ce que tu as lu ? ».
    const empreinte = r.geste ? empreinteDeGeste(r.geste) : null;
    // ⚠️ « DÉJÀ PRÉPARÉ » N'EST PAS « PLUS RIEN » (revue adverse #1432,
    // MAJEUR 3). Quand le serveur retire une carte parce qu'elle est DÉJÀ sur
    // la table, la réponse arrive sans geste — mais la carte, elle, est
    // toujours là. L'effacer rendait le geste définitivement inatteignable :
    // plus de carte à cliquer, et un « oui » qui répond « je n'ai rien en
    // attente ». Le motif voyage donc avec le tour, et l'écran le consomme.
    // ⚠️ TOUS LES MOTIFS, PAS SEULEMENT « DÉJÀ PRÉPARÉ » (lot 0, 2026-09-22).
    // Le serveur retire aussi une carte quand il n'a pas su dire de quel
    // logement on parle — et cette phrase-là, il la PRÉFIXE au texte, donc
    // Dimitri la lit. Ne garder que l'un des deux motifs laissait le fil croire
    // qu'une carte était encore en attente : son « oui » suivant aurait porté
    // sur la carte d'avant.
    const motifRetrait = motifDuRetrait(r);
    ajouterAuFil("lui", r.texte || "", {
      ...(r.trace ? { trace: r.trace } : {}),
      // Le geste reste ATTACHÉ à son tour : c'est ce qui permet au « oui » du
      // tour suivant de savoir à quoi il répond, même après un rechargement.
      // ⚠️ Il passe donc par `sessionStorage` (le fil y est recopié) : ses
      // arguments y vivent le temps de l'onglet. Ils n'y avaient jamais mis
      // les pieds avant ce lot — à garder en tête si un levier venait à porter
      // un secret dans ses arguments (aucun aujourd'hui : ce sont des
      // identifiants, des dates, des nombres, et des textes relus par Dimitri).
      ...(r.geste ? { geste: r.geste, geste_empreinte: empreinte } : {}),
      ...(motifRetrait ? { geste_retire: motifRetrait } : {}),
      // ⚠️ L'ÉTAT DU FORMULAIRE EST ATTACHÉ AU TOUR, et il est posé DÈS QUE LE
      // SERVEUR EN PARLE — y compris à `null`. Ne le poser que lorsqu'il est
      // vrai ferait remonter l'état d'avant au tour suivant
      // (`formulaireEnCours` remonte le fil) : la conversation reprendrait un
      // formulaire que Dimitri vient d'annuler, ou re-proposerait la carte
      // d'un formulaire déjà clôturé.
      ...(r.formulaire !== undefined ? { formulaire: r.formulaire } : {}),
      // ⚠️ ATTACHÉ À CHAQUE TOUR, `null` COMPRIS — l'inverse du formulaire, et
      // pour une raison précise : une confirmation de nom ne vit QU'UN tour.
      // Ne la poser que lorsqu'elle existe ferait remonter celle d'avant, et
      // un « oui » prononcé trois questions plus tard armerait une carte dont
      // il n'était plus question.
      confirmation_attendue: r.confirmation_attendue || null,
    });
    // Une nouvelle réponse remplace la carte — ou la retire s'il n'en prépare
    // plus : une carte d'il y a trois questions n'est plus ce qu'on regarde.
    // Sauf « déjà préparé » : c'est la MÊME carte, elle reste, et elle le dit.
    setCarte((c) =>
      r.geste
        ? { geste: r.geste }
        : motifRetrait === DEJA_PREPARE && c
          ? { ...c, rappel: RAPPEL_DEJA_PREPARE }
          : null,
    );
    // ⚠️ On ne LIT PAS ici : la lecture à voix haute appartient au mode
    // conversation, qui l'enchaîne lui-même et sait la couper. Lire depuis
    // les deux endroits ferait parler deux fois.

    // ── MONTRER : on va chercher les chiffres, et on DIT qu'on les cherche ──
    // ⚠️ Le serveur a choisi la vue ; il n'a pas rassemblé ses chiffres (cf.
    // src/lib/vues-donnees.js — ils ne passent jamais devant le modèle).
    if (r.registre === "montrer" && r.vue) {
      setVueDonnees({ etat: "en_cours" });
      const d = await chargerDonneesVue(r.vue, { supabase });
      setVueDonnees(
        d.ok
          ? { etat: "prêt", donnees: d.donnees, partiel: d.partiel }
          : { etat: "absent", motif: d.motif },
      );
    } else {
      setVueDonnees(null);
    }
    // « Il exécute une demande, il propose une suggestion » : seule une
    // demande explicite déplace l'écran, et c'est le SERVEUR qui l'a tranché.
    //
    // ⚠️ SAUF À LA VOIX. Réserve de Dimitri (2026-09-09) : « ce qui agit ne
    // doit jamais partir à la voix ». Une phrase mal entendue ne doit pas non
    // plus DÉPLACER son écran — surtout s'il conduit et ne regarde pas. En
    // conversation, `sollicite` devient une PORTE : le bouton s'affiche, et
    // c'est son clic qui ouvre.
    if (
      r.registre === "emmener" &&
      r.destination?.sollicite === true &&
      !enVocalRef.current &&
      onAller
    ) {
      onAller(r.destination);
    }
    return r;
  }

  /** L'entrée CLAVIER : elle gère le champ et l'état du bouton. */
  async function parler() {
    const q = brouillon.trim();
    if (!q || enCours) return;
    setEnCours(true);
    try {
      await envoyerQuestion(q);
      setBrouillon("");
    } finally {
      setEnCours(false);
    }
  }
  const rienNePresse = !!aFaire && (aFaire.lignes || []).length === 0;

  // ── CE QU'IL MONTRE, CE QU'IL RANGE, CE QUI RESTE À FAIRE ──────────────
  //
  // ⚠️ TOUT DÉCOULE D'UNE SEULE LISTE. La journée accumulée, pesée au JOUR DE
  // L'ÉCRAN (le barème est le même que côté serveur, octet pour octet) : le
  // fil en est une vue, le bouton des remarques une autre, la feuille « à
  // traiter » une troisième. Un compteur et sa liste ne peuvent pas diverger
  // s'ils sortent du même calcul — c'est exactement ce qui clochait quand la
  // pastille disait « 3 » au-dessus d'une liste vide.
  const presse = (e) => estEnCarte(e, { jour });
  const duFil = vueDuFil(journee);
  const filRendu = duFil.map((e) => ({
    ...e,
    presse: presse(e),
    heure: heureDe(e),
  }));
  const cartes = filRendu.filter((e) => e.presse);
  // ── LE BOUTON GARDE TOUT, Y COMPRIS CE QU'IL A FERMÉ ───────────────────
  // Tout ce qui ne presse pas (rangé, encore en trait dans le fil, ou traité)
  // ET les cartes que Dimitri a traitées : elles ne sont plus dans le fil, et
  // sans ça elles n'étaient NULLE PART — alors que la règle est « rien ne
  // s'efface, tout se range » (revue, m11).
  const remarques = journee.filter((e) => !presse(e) || e.traite);
  const nRemarques = remarques.length;
  // ── LA LISTE UNIQUE (Dimitri, 2026-09-15) ──────────────────────────────
  // « Mes tâches en retard et mes événements du jour sont la même chose de
  // mon point de vue — des choses à faire ; les séparer m'oblige à regarder à
  // deux endroits. » La pastille COMPTE cette liste-là, elle ne recompte rien.
  const aTraiter = composerATraiter({ aFaire, evenements: duFil, jour });
  const nATraiter = aTraiter.length;

  // ── SES TROIS ÉTATS ────────────────────────────────────────────────────
  // Dimitri, 2026-09-13 : « Vert quand tout va bien, terracotta quand il y a
  // de l'urgent, bleu quand il cherche. Il bat lentement quand il est chaud. »
  //
  // ⚠️ L'ÉTAT SE DÉDUIT DE CE QUI EST À L'ÉCRAN, il ne se décide pas ailleurs.
  // Un point vert au-dessus d'une liste de trois urgences serait exactement le
  // genre de mensonge tranquille qu'on passe nos journées à retirer.
  // ⚠️ LA PASTILLE COMPTE LA LISTE, PAS AUTRE CHOSE (maquette `nbUrgents`) :
  // « un compteur et sa liste doivent toujours dire la même chose ».
  const nUrgent = nATraiter;
  const etatOp = enCours ? "cherche" : nUrgent > 0 ? "urgent" : "calme";
  const COULEUR_ETAT = { urgent: TERRA, cherche: CIEL, calme: VERT };
  // ⚠️ « RIEN » NE SE DIT QUE SI ON A LU. `aFaire` absent = l'écran n'a pas
  // encore composé sa liste : on ne conclut pas au calme, on ne dit rien.
  const etatLisible = !!aFaire;
  const motEtat = !etatLisible
    ? "…"
    : etatOp === "cherche"
      ? "il cherche"
      : nUrgent > 0
        ? `${nUrgent} à traiter`
        : "tout est à jour";

  // ── LA SPHÈRE ET SON SOUS-TITRE (maquette, `tete(cl, sous, vif, past)`) ──
  // Trois états de sphère : `dort` (gris, immobile) quand rien ne presse,
  // `cherche` (respire) pendant qu'il travaille, `veille` sinon. Le sous-titre
  // sous son nom dit ce qu'il FAIT quand il cherche (« je lis tes messages… »
  // — le vrai texte du serveur, pas un tiret), et sinon ce qu'il y a :
  // combien de choses de neuf, ou le mot d'état. Aucune donnée nouvelle.
  //
  // ⚠️ LE SOUS-TITRE DIT CE QUE L'ÉCRAN MONTRE, ET RIEN D'AUTRE (Dimitri,
  // 2026-09-15 : « "26 points à régler" en haut, 18 événements affichés.
  // Lequel est vrai ? »). Deux comptes, donc : ce que le fil prend en charge,
  // et ce que « il remarque » contient. Pas un troisième calculé ailleurs.
  const nEvenements = filRendu.length + nRemarques;
  const etatOrbe = enCours ? "cherche" : rienNePresse ? "dort" : "veille";
  const nCartes = cartes.length;
  const compteFil = [
    nCartes > 0 ? `${nCartes} qui presse${nCartes > 1 ? "nt" : ""}` : null,
    nRemarques > 0
      ? `${nRemarques} remarque${nRemarques > 1 ? "s" : ""}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const sousTitre = enCours
    ? etape
    : compteFil
      ? compteFil
      : etatOp === "urgent"
        ? motEtat
        : etatLisible
          ? rienNePresse
            ? "rien de neuf"
            : "il veille"
          : "il n'a pas encore lu votre écran";
  // ── RIEN D'OUVERT (`.opNu`) ────────────────────────────────────────────
  // En veille, sans conversation, sans détail déplié, sans événement : sa
  // sphère seule au centre, en 64px, « appuyez pour lui parler ». On ne
  // conclut « rien d'ouvert » que si l'écran a été lu.
  const nu =
    etatOrbe === "veille" &&
    etatLisible &&
    !detOuvert &&
    nEvenements === 0 &&
    fil.length === 0 &&
    !reponse &&
    !enTrain &&
    !avis &&
    !gesteEvenement;

  // ── UN TOUR DE CONVERSATION, DESSINÉ ───────────────────────────────────
  // ⚠️ EXTRAIT DU FIL POUR QU'IL PUISSE SE MÊLER AUX ÉVÉNEMENTS (2026-09-15).
  // Dimitri : « Les 18 événements sont empilés en bloc juste au-dessus de la
  // barre de saisie, après toute ma conversation. Ils devraient être DANS le
  // fil, chacun à son heure, mêlés à ce qu'on s'est dit. UNE SEULE
  // CHRONOLOGIE. C'est ce qui fait la différence entre une conversation et un
  // tableau de bord avec une zone de chat en haut. »
  //
  // Le dessin n'a pas changé d'une ligne : seule sa PLACE se décide ailleurs
  // (`FilEvenements` fusionne les tours et les annonces par leur heure).
  function rendreTour(t, i) {
    return t.role === "fait" ? (
      /* ── CE QU'UN CLIC A DONNÉ ─────────────────────────────
                     Ni une bulle à droite (ce n'est pas lui qui parle), ni un
                     tour à gauche avec la sphère (l'opérateur ne l'a pas dit) :
                     une ligne sobre, qui se lit comme un accusé. */
      <div
        // eslint-disable-next-line react/no-array-index-key
        key={`${t.quand}-${i}`}
        data-role="fait"
        data-testid="operateur-fil-fait"
        style={{
          alignSelf: "stretch",
          fontSize: 11.5,
          lineHeight: 1.5,
          color: GRIS_D,
          padding: "6px 10px",
          borderLeft: `2px solid ${CIEL}`,
          background: "#f6f9fc",
          borderRadius: 6,
        }}
      >
        {t.texte}
      </div>
    ) : t.role === "moi" ? (
      <div
        // eslint-disable-next-line react/no-array-index-key
        key={`${t.quand}-${i}`}
        data-role="moi"
        style={{
          alignSelf: "flex-end",
          maxWidth: "78%",
          background: NAVY_D,
          color: "#fff",
          borderRadius: "14px 14px 4px 14px",
          padding: "8px 12px",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        {t.texte}
        <span
          style={{
            display: "block",
            marginTop: 3,
            fontSize: 9.5,
            opacity: 0.6,
          }}
        >
          {new Date(t.quand).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
    ) : (
      <div
        // eslint-disable-next-line react/no-array-index-key
        key={`${t.quand}-${i}`}
        data-role="lui"
        style={{
          alignSelf: "flex-start",
          maxWidth: "93%",
          display: "flex",
          gap: 9,
        }}
      >
        <Pastille taille={23} fond={CIEL} encre="#fff" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.6,
              color: NAVY_D,
            }}
          >
            {t.texte}
          </div>
          <span
            style={{
              fontSize: 9.5,
              color: GRIS_D,
              marginTop: 2,
              display: "block",
            }}
          >
            {new Date(t.quand).toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      </div>
    );
  }

  // Le point d'état (`.opPoint`, 9px) et son halo par couleur — maquette :
  //   .urgent  terra + 0 0 0 3px rgba(143,47,29,.14)
  //   .attente ciel  + 0 0 0 3px rgba(135,184,230,.2)
  //   .calme   vert  + 0 0 0 3px rgba(47,158,110,.15)
  const HALO_ETAT = {
    urgent: "0 0 0 3px rgba(143,47,29,.14)",
    cherche: "0 0 0 3px rgba(135,184,230,.2)",
    calme: "0 0 0 3px rgba(47,158,110,.15)",
  };

  return (
    <div
      data-testid="bloc-operateur"
      data-nu={nu ? "1" : undefined}
      style={{
        // `.opBloc2` : dégradé blanc, rayon 22, SANS bordure, ombre en deux
        // couches — le bloc se pose sur la page, il n'y est pas découpé.
        background: "linear-gradient(180deg,#fff,#fbfcfe)",
        borderRadius: 22,
        margin: "0 0 12px",
        overflow: "hidden",
        boxShadow:
          "0 1px 3px rgba(35,50,64,.05), 0 14px 40px -20px rgba(35,50,64,.28)",
        fontFamily: DS.fontBody,
        color: NAVY_D,
      }}
    >
      <style>{STYLE_OP}</style>
      {/* ── LA TÊTE (`.opTete2`) — transparente, PLUS de bandeau ciel ────
          La sphère, son nom en sombre avec un sous-titre dessous, et le point
          d'état à droite. Quand rien n'est ouvert, la sphère descend au centre
          du bloc et la tête ne garde que le point. */}
      <div
        data-testid="operateur-tete"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: nu ? "15px 17px 0" : "15px 17px",
        }}
      >
        {nu ? (
          <span style={{ flex: 1 }} />
        ) : (
          <>
            <Orbe etat={etatOrbe} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <b
                style={{
                  display: "block",
                  fontSize: 13.5,
                  fontWeight: 600,
                  letterSpacing: "-.015em",
                  color: NAVY_D,
                }}
              >
                L&rsquo;opérateur
              </b>
              <span
                data-testid="operateur-sous-titre"
                style={{
                  display: "block",
                  fontSize: 11,
                  marginTop: 2,
                  ...(etatOp === "urgent" && !enCours
                    ? { color: TERRA_TX, fontWeight: 600 }
                    : { color: GRIS_D }),
                }}
              >
                {sousTitre}
              </span>
            </span>
          </>
        )}

        {/* ── « IL REMARQUE » — LÀ OÙ VA CE QUI NE PRESSE PAS ────────────
            Dimitri, 2026-09-15 : « Le bouton "Repartir de zéro" devient "il
            remarque" — c'est là que va tout ce qui n'est pas urgent. » Les
            factures déjà émises, les courriels classés, les messages partis,
            les codes promo, la synchro : des constats, pas des choses à faire.
            ⚠️ Ils sont RANGÉS, pas jetés — le compte est sur le bouton, et le
            panneau les rend en entier, dans l'ordre du temps. */}
        {nRemarques > 0 && (
          <button
            type="button"
            data-testid="operateur-il-remarque"
            aria-expanded={remarquesOuvertes}
            onClick={() => setRemarquesOuvertes((v) => !v)}
            title="Ce qu'il a remarqué, sans rien à faire"
            style={{
              height: 27,
              padding: "0 11px",
              borderRadius: 14,
              border: "none",
              background: FOND,
              color: GRIS_D,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
              flexShrink: 0,
            }}
          >
            ◈ il remarque · {nRemarques}
          </button>
        )}

        {/* ── LE POINT D'ÉTAT — en haut à droite de son bloc ─────────────
            Un rond, trois couleurs, et il BAT quand c'est chaud. Le clic
            déplie la liste compacte : sept lignes, leur vrai geste.
            Pastille `.opTete2 .n` : 27px de haut, rayon 14 ; `.rien` sur fond
            clair, `.att` terra pâle avec son liseré quand c'est urgent. */}
        <button
          type="button"
          data-testid="operateur-point"
          aria-expanded={detOuvert}
          onClick={() => setDetOuvert((v) => !v)}
          title={
            etatLisible
              ? "Ce qu'il y a à faire"
              : "Il n'a pas encore lu votre écran"
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 27,
            padding: "0 11px",
            borderRadius: 14,
            border: "none",
            ...(etatOp === "urgent"
              ? {
                  background: PALETTE_OP.terraBg,
                  color: TERRA_TX,
                  boxShadow: `inset 0 0 0 1px ${PALETTE_OP.terraBd}`,
                }
              : { background: FOND, color: GRIS_D }),
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: DS.fontBody,
            flexShrink: 0,
          }}
        >
          {/* ⚠️ L'ANIMATION S'ARRÊTE SI LE SYSTÈME LA REFUSE (règle Dimitri :
              « tout s'arrête si le système demande la réduction des
              animations »). Le point reste, il cesse seulement de battre. */}
          <style>{`@keyframes fbzOpBat{0%,100%{box-shadow:0 0 0 0 rgba(143,47,29,.45)}60%{box-shadow:0 0 0 6px rgba(143,47,29,0)}}
@media (prefers-reduced-motion:reduce){[data-fbz-bat]{animation:none!important}}`}</style>
          <span
            data-testid="operateur-point-rond"
            data-etat={etatOp}
            data-fbz-bat={etatOp === "urgent" ? "1" : undefined}
            aria-hidden="true"
            style={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              flexShrink: 0,
              marginRight: 2,
              background: etatLisible ? COULEUR_ETAT[etatOp] : "#b9c9d8",
              boxShadow: etatLisible ? HALO_ETAT[etatOp] : undefined,
              ...(etatOp === "urgent"
                ? { animation: "fbzOpBat 2.2s ease-in-out infinite" }
                : {}),
            }}
          />
          <span>{motEtat}</span>
        </button>
      </div>

      {/* ── RIEN D'OUVERT : SA SPHÈRE SEULE, AU CENTRE (`.opNu`) ───────────
          On appuie, on lui parle — le même geste que le micro d'en bas. */}
      {nu && (
        <div
          data-testid="operateur-nu"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "34px 17px 30px",
          }}
        >
          <button
            type="button"
            className="fbzOpNu"
            data-testid="operateur-nu-parler"
            aria-pressed={enVocal}
            aria-label={
              enVocal
                ? "Fermer la conversation vocale"
                : "Parler avec l'opérateur"
            }
            onClick={basculerVocal}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 13,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontFamily: DS.fontBody,
              padding: 0,
            }}
          >
            <Orbe etat="veille" taille={64} cerveau={27} />
            <span style={{ fontSize: 12.5, color: GRIS_D }}>
              {enVocal ? "je vous écoute…" : "appuyez pour lui parler"}
            </span>
          </button>
        </div>
      )}

      {/* ══ CE QUI RESTE À FAIRE — UNE SEULE LISTE ══════════════════════
          Dimitri, 2026-09-15 : « mes tâches en retard et mes événements du
          jour sont la même chose de mon point de vue — des choses à faire ;
          les séparer m'oblige à regarder à deux endroits. »
          Les états durables et les annonces qui pressent, FUSIONNÉS, triés
          par ce que ça coûte d'attendre (le fil, lui, suit la chronologie).
          La pastille en compte les lignes : un compteur et sa liste disent
          toujours la même chose.
          ⚠️ ET ELLE NE SE FERME PAS POUR AGIR : « si je clique sur une action
          et que je perds ma liste, je ne reviens pas la finir. » */}
      {detOuvert && (
        <div
          data-testid="operateur-detail"
          style={{
            padding: "0 13px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <style>{`@keyframes fbzOpMonte{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion:reduce){[data-fbz-monte]{animation:none!important;opacity:1!important}}`}</style>
          {aTraiter.length === 0 && (
            <span
              data-testid="operateur-detail-vide"
              style={{ padding: "10px 12px", fontSize: 12, color: GRIS_D }}
            >
              {etatLisible
                ? "Rien à traiter. Tout est à jour — je vous prierai de ne pas vous y habituer."
                : "Il n'a pas encore lu votre écran."}
            </span>
          )}
          {aTraiter.map((l, i) => {
            const ton = tonDuPoids(l.poids);
            const fond =
              ton === "chaud"
                ? PALETTE_OP.terraBg
                : ton === "tiede"
                  ? "#fdf6e8"
                  : FOND;
            const encre =
              ton === "chaud" ? TERRA_TX : ton === "tiede" ? "#8a5a12" : GRIS_D;
            return (
              <div
                key={l.cle}
                data-testid={`operateur-detail-${l.cle}`}
                data-fbz-monte="1"
                data-ton={ton}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 11,
                  padding: "10px 4px",
                  borderTop: i ? `1px solid ${PALETTE_OP.bord}` : "none",
                  opacity: 0,
                  animation: `fbzOpMonte .3s cubic-bezier(.22,1,.36,1) ${(i * 0.045).toFixed(3)}s forwards`,
                }}
              >
                {/* Le rang, dans son rond — et sa couleur DIT le poids. */}
                <span
                  aria-hidden="true"
                  data-testid={`operateur-detail-rang-${l.cle}`}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    flexShrink: 0,
                    marginTop: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 700,
                    background: fond,
                    color: encre,
                  }}
                >
                  {i + 1}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 7,
                      marginBottom: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 9,
                        letterSpacing: ".07em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        color: ton === "chaud" ? TERRA_TX : GRIS_D,
                      }}
                    >
                      {l.nature}
                    </span>
                    {l.heure ? (
                      <span style={{ fontSize: 9, color: GRIS_D }}>
                        {l.heure}
                      </span>
                    ) : null}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      color: NAVY_D,
                    }}
                  >
                    {l.texte}
                  </span>
                  {/* LE GESTE SE DÉPLIE ICI, SOUS SA LIGNE. La feuille reste
                      ouverte : on en traite trois d'affilée sans la quitter. */}
                  {gesteDeLaListe === l.cle && l.geste ? (
                    <span
                      data-testid={`operateur-detail-geste-${l.cle}`}
                      style={{ display: "block", marginTop: 8 }}
                    >
                      {/* ⚠️ `onFait` — SANS LUI, LE GESTE NE LAISSE AUCUNE
                          TRACE (revue, MAJEUR 6). Les deux autres montages de
                          cette carte le passent ; celui-ci ne le passait pas :
                          le geste partait, rien n'entrait au fil, la ligne
                          restait dans la liste et la pastille ne décroissait
                          pas. Dimitri l'aurait refait une deuxième fois. */}
                      <CarteGesteDemande
                        geste={l.geste}
                        onFait={(g, r) => {
                          noterFait(g, r);
                          // Fait = traité : la ligne sort de la liste et du
                          // fil, et reste dans « votre journée ».
                          if (r && r.ok) {
                            traiterEvenement(l.cle);
                            setGesteDeLaListe(null);
                          }
                        }}
                      />
                    </span>
                  ) : null}
                </span>
                {l.onTraite ? (
                  <button
                    type="button"
                    data-testid={`operateur-detail-traite-${l.cle}`}
                    onClick={l.onTraite}
                    title="Rien à répondre ?"
                    aria-label={`Marquer traité : ${l.texte}`}
                    style={{
                      minWidth: 32,
                      minHeight: 32,
                      borderRadius: 9,
                      border: "none",
                      background: "transparent",
                      color: GRIS_D,
                      fontSize: 13,
                      cursor: "pointer",
                      flexShrink: 0,
                      fontFamily: DS.fontBody,
                    }}
                  >
                    ✓
                  </button>
                ) : null}
                <button
                  type="button"
                  data-testid={`operateur-detail-action-${l.cle}`}
                  onClick={() => {
                    // ⚠️ AUCUNE FERMETURE AVANT UNE ACTION. Un geste se
                    // déplie sur place ; un état durable ouvre sa feuille ;
                    // seul un DÉPLACEMENT d'écran referme celle-ci — et elle
                    // se rouvrira au retour (`operateur-detail-ouvert`).
                    if (l.geste) {
                      setGesteDeLaListe((v) => (v === l.cle ? null : l.cle));
                      return;
                    }
                    if (l.onClic) {
                      l.onClic();
                      return;
                    }
                    if (l.ecran && onAller) {
                      try {
                        window.sessionStorage.setItem(
                          "fbz-a-traiter-ouvert",
                          "1",
                        );
                      } catch (e) {
                        /* sans mémoire, on perd la réouverture, pas la liste */
                      }
                      setDetOuvert(false);
                      onAller({ ecran: l.ecran });
                    }
                  }}
                  style={{
                    flexShrink: 0,
                    minHeight: 30,
                    padding: "0 11px",
                    borderRadius: 9,
                    background: NAVY_D,
                    border: "none",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    fontFamily: DS.fontBody,
                  }}
                >
                  {l.libelle}
                </button>
              </div>
            );
          })}
          <button
            type="button"
            data-testid="operateur-voir-tout"
            onClick={() => {
              setDetOuvert(false);
              if (onVoirTout) onVoirTout();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: 38,
              marginTop: 5,
              border: "none",
              background: "transparent",
              fontSize: 11.5,
              color: GRIS_D,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            {cartesOuvertes
              ? "Masquer les cartes"
              : "Montrer tout ce qu’il y a à faire ›"}
          </button>
        </div>
      )}

      {/* ── LE CORPS (`.opCorps{padding:0 17px 6px;gap:15px}`) ───────────
          Sa phrase, la conversation, ce qu'il annonce, sa réponse : tout vit
          DANS le bloc, sans fond ni bordure entre les parties. Les événements
          formaient une section grise prise entre deux blocs blancs bordés —
          c'est exactement ce que Dimitri ne reconnaissait pas. */}
      <div
        data-testid="operateur-corps"
        style={{
          padding: "0 17px 6px",
          display: "flex",
          flexDirection: "column",
          gap: 15,
        }}
      >
        {/* ── LE PANNEAU « IL REMARQUE » — MONTÉ EN PERMANENCE ─────────
            Ouvert par le bouton de l'en-tête, il liste TOUT ce qui ne presse
            pas : ce que le temps a rangé, ce qui est encore en trait dans le
            fil, et ce qui a été traité (marqué « traité »).
            ⚠️ IL RESTE MONTÉ, CACHÉ. Le démonter remettait à zéro les lignes
            déjà vues : à chaque réouverture, tout rejouait son animation
            d'entrée comme si c'était neuf. `hidden` coûte moins qu'un état
            reconstruit — et il ne ment pas. */}
        <div data-testid="operateur-remarques" hidden={!remarquesOuvertes}>
          {nRemarques > 0 ? (
            <>
              <span
                style={{
                  display: "block",
                  fontSize: 11.5,
                  color: GRIS,
                  lineHeight: 1.6,
                  marginBottom: 6,
                }}
              >
                Rien ici ne presse. Ce sont des constats — je les garde pour que
                vous puissiez y revenir.
              </span>
              <FilEvenements
                testid="operateur-remarques-liste"
                evenements={remarques.map((e) => ({
                  ...e,
                  presse: false,
                  heure: heureDe(e),
                }))}
                onGeste={(e) =>
                  setGesteEvenement((v) => (v && v.cle === e.cle ? null : e))
                }
                onAller={(e) =>
                  onAller && e.ecran && onAller({ ecran: e.ecran })
                }
                onTraiter={(cle) => traiterEvenement(cle)}
                onOuvrir={(e) => setFeuilleEvenement(e)}
              />
              {coupes > 0 && (
                <span
                  data-testid="operateur-remarques-coupes"
                  style={{
                    display: "block",
                    fontSize: 11,
                    color: GRIS_D,
                    marginTop: 8,
                  }}
                >
                  Et {coupes} constat{coupes > 1 ? "s" : ""} de plus, que je
                  n'ai pas rapporté{coupes > 1 ? "s" : ""} à l'écran.
                </span>
              )}
            </>
          ) : null}
        </div>

        {/* ── PLUS DE BANDEAU AU-DESSUS DE LA CONVERSATION (2026-09-15) ──
            « Le bandeau du haut ne sert à rien. Retire-le. » Ce qu'il y avait
            à dire est là où ça se lit : le fil pour ce qui presse, « il
            remarque » pour les constats, le sous-titre pour le compte. */}
        <div>
          {/* Le compte n'apparaît QUE s'il y a quelque chose à accorder. */}
          {nbPropositions > 0 && (
            <button
              data-testid="operateur-accord"
              onClick={onOuvrir}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                minHeight: 44,
                marginTop: 6,
                fontSize: 11.5,
                color: NAVY,
                fontWeight: 600,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: DS.fontBody,
                padding: 0,
              }}
            >
              {nbPropositions} réponse{nbPropositions > 1 ? "s" : ""} attend
              {nbPropositions > 1 ? "ent" : ""} votre accord ›
            </button>
          )}

          {/* L'opérateur qui dort se dit — jamais un silence qui ressemblerait
            à « rien à décider » (règle déjà en vigueur sur cet écran). */}
          {motifDormant && (
            <span
              data-testid="operateur-dormant"
              style={{
                display: "block",
                fontSize: 10.5,
                color: GRIS,
                marginTop: 8,
                lineHeight: 1.5,
              }}
            >
              {motifDormant}
            </span>
          )}
        </div>

        {/* ── LA CONVERSATION EN COURS ──────────────────────────────────
          Bande terracotta : l'état, et ce qu'il entend en direct. Le micro
          reste OUVERT pendant qu'il parle — c'est ce qui permet de le couper. */}
        {enVocal && (
          <div
            data-testid="operateur-ecoute"
            data-etat={vocal}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 14px",
              background: "#F6EDE7",
              borderRadius: 12,
              fontSize: 12.5,
              color: "#8A4B2A",
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#C17B5C",
                flexShrink: 0,
                opacity: vocal === "ecoute" ? 1 : 0.4,
              }}
            />
            <span style={{ flex: 1, minWidth: 0, fontStyle: "italic" }}>
              {entendu ||
                {
                  ecoute: "Je vous écoute…",
                  envoi: "Je cherche…",
                  parle: "Je réponds — coupez-moi quand vous voulez.",
                }[vocal] ||
                "…"}
            </span>
          </div>
        )}

        {/* ⚠️ IL NE MEUBLE PAS : écho, micro refusé, écoute qui lâche — il le DIT.
          Un mode ouvert et sourd est le pire des états, parce qu'il a l'air de
          marcher. */}
        {avis && (
          <div
            data-testid="operateur-vocal-avis"
            style={{
              fontSize: 11.5,
              color: GRIS,
            }}
          >
            {avis}
          </div>
        )}

        {/* ── LE FIL — mes questions à DROITE, les siennes à GAUCHE ───────
          Habillage de la maquette : bulle navy à droite, texte nu à gauche
          avec sa pastille, l'heure sous chaque tour. On n'affiche PAS le
          dernier tour de l'opérateur — il est rendu juste dessous, en entier,
          avec sa vue et sa porte ; deux fois donnerait l'impression qu'il se
          répète. */}
        {/* ⚠️ LA CONDITION SUIT CE QUI SERA RÉELLEMENT AFFICHÉ, pas un compte
          fixe. Elle disait `fil.length > 1` — vrai tant qu'un fil commençait
          toujours par une question de Dimitri. Depuis que l'opérateur PARLE LE
          PREMIER (le brief), un fil d'un seul tour existe : son mot d'ouverture
          restait invisible, et rien ne le signalait. */}
        {/* ⚠️ ON RETIRE LA DERNIÈRE RÉPONSE PAR SON RÔLE, PAS PAR SA PLACE
          (2026-09-15). `slice(0, -1)` supposait que le dernier tour ÉTAIT la
          réponse ; depuis qu'un compte rendu de geste peut la suivre, il
          coupait le compte rendu et laissait la réponse — affichée deux fois,
          une fois ici et une fois en entier dessous. */}
        {toursAffiches.length > 0 || filRendu.length > 0 || enCours ? (
          <div
            data-testid="operateur-fil"
            style={{
              maxHeight: 320,
              overflowY: "auto",
              padding: "0 0 4px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* ⚠️ UNE SEULE CHRONOLOGIE : les tours et les annonces se
              fondent ici, chacun à son heure (cf. `filEvenements` de la
              maquette). Un bloc d'événements posé au-dessus de la barre de
              saisie, c'est un tableau de bord avec une zone de chat — pas une
              conversation. */}
            <FilEvenements
              evenements={filRendu}
              tours={toursAffiches}
              rendreTour={rendreTour}
              onGeste={(e) =>
                setGesteEvenement((v) => (v && v.cle === e.cle ? null : e))
              }
              onAller={(e) => onAller && e.ecran && onAller({ ecran: e.ecran })}
              onTraiter={(cle) => traiterEvenement(cle)}
              onRanger={(cle) => rangerEvenement(cle)}
              onOuvrir={(e) => setFeuilleEvenement(e)}
            />

            {/* ── LES TROIS POINTS — il réfléchit ─────────────────────────
              Une attente muette est indiscernable d'une panne. */}
            {/* ⚠️ LES TROIS POINTS DÈS LE PREMIER MOT TAPÉ, PAS À PARTIR DU
              DEUXIÈME ÉCHANGE. Ils étaient enfermés dans un bloc rendu
              seulement quand le fil comptait déjà deux tours : à la PREMIÈRE
              question, rien ne bougeait pendant les cinq secondes que met le
              modèle à répondre. Une attente muette est indiscernable d'une
              panne — et c'est exactement ce que Dimitri a signalé. */}
            {enCours && !enTrain && (
              <div
                data-testid="operateur-attend"
                style={{
                  alignSelf: "flex-start",
                  display: "flex",
                  gap: 9,
                  alignItems: "center",
                }}
              >
                <Pastille taille={23} fond={CIEL} encre="#fff" />
                <TroisPoints />
                {/* Il DIT ce qu'il fait : « je rassemble » n'est pas « il rame ». */}
                <span style={{ fontSize: 10.5, color: GRIS_D }}>{etape}</span>
              </div>
            )}
          </div>
        ) : null}

        {/* ⚠️ UNE LECTURE EN PANNE SE DIT (m12). « Rien qui presse » et « je
          n'ai pas pu lire » ont la même tête sur un écran muet — et l'un des
          deux est un mensonge tranquille. */}
        {annoncesMotif ? (
          <span
            data-testid="operateur-annonces-panne"
            style={{ fontSize: 11.5, color: TERRA_TX, lineHeight: 1.5 }}
          >
            {annoncesMotif}
          </span>
        ) : null}

        {/* ── VOTRE JOURNÉE — TOUT, Y COMPRIS CE QUI EST TRAITÉ ──────────
          Maquette `opJournee` : à son heure, barré quand c'est traité, et
          « rouvrir » pour le reprendre. À minuit, le traité disparaît et le
          reste vieillit d'un jour. */}
        {journee.length > 0 && (
          <button
            type="button"
            data-testid="operateur-journee-lien"
            onClick={() => setJourneeOuverte((v) => !v)}
            style={{
              alignSelf: "flex-start",
              minHeight: 28,
              padding: 0,
              border: "none",
              background: "none",
              color: GRIS,
              fontSize: 11,
              textDecoration: "underline",
              textUnderlineOffset: 3,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            {journeeOuverte
              ? "masquer votre journée"
              : `votre journée · ${journee.length}`}
          </button>
        )}
        {journeeOuverte && (
          <div
            data-testid="operateur-journee-liste"
            style={{
              border: `1px solid ${PALETTE_OP.bord}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            {[...journee]
              .sort((a, b) => String(a.quand).localeCompare(String(b.quand)))
              .map((e, i) => {
                const age = ageEnJours(e, jour);
                return (
                  <div
                    key={e.cle}
                    data-testid={`operateur-journee-${e.cle}`}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "10px 12px",
                      borderTop: i ? `1px solid ${PALETTE_OP.bord}` : "none",
                      opacity: e.traite ? 0.5 : 1,
                    }}
                  >
                    <span
                      style={{
                        width: 52,
                        flexShrink: 0,
                        fontSize: 10,
                        color: GRIS_D,
                        marginTop: 2,
                      }}
                    >
                      {age === 0
                        ? heureDe(e)
                        : age === 1
                          ? `hier ${heureDe(e)}`
                          : `il y a ${age} jours`}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 11.5,
                        lineHeight: 1.5,
                        color: NAVY_D,
                        textDecoration: e.traite ? "line-through" : "none",
                      }}
                    >
                      {e.phrase}
                    </span>
                    {e.traite ? (
                      <button
                        type="button"
                        data-testid={`operateur-journee-rouvrir-${e.cle}`}
                        onClick={() => rouvrirEvenement(e.cle)}
                        style={{
                          flexShrink: 0,
                          fontSize: 10,
                          color: GRIS_D,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontFamily: DS.fontBody,
                        }}
                      >
                        rouvrir
                      </button>
                    ) : null}
                  </div>
                );
              })}
            <span
              style={{
                display: "block",
                padding: "9px 12px",
                fontSize: 10.5,
                color: GRIS_D,
                background: FOND,
              }}
            >
              À minuit, ce qui reste passe à demain. Ce qui est traité
              disparaît.
            </span>
          </div>
        )}

        {/* ── EFFACER LA CONVERSATION — DISCRET, EN BAS DU FIL ───────────
          Le bouton d'en-tête est devenu « il remarque » (2026-09-15). Effacer
          reste possible, mais ce n'est pas une décision qu'on prend en
          arrivant : un lien texte, sous ce qu'il efface. Le nom du repère ne
          change pas — des scripts de vérification le désignent. */}
        {fil.length > 1 && (
          <button
            type="button"
            data-testid="operateur-fil-vider"
            onClick={() => {
              viderFil();
              setReponse(null);
              // ⚠️ ET LES CARTES AVEC (revue adverse #1432, MAJEUR 4b). Une
              // carte survit désormais à la question suivante : sans ces deux
              // lignes, effacer la conversation laissait un geste ARMÉ
              // au-dessus d'un fil vide — et un « oui » l'aurait lancé.
              setCarte(null);
              setGesteEvenement(null);
            }}
            title="Effacer la conversation en cours"
            style={{
              alignSelf: "flex-start",
              minHeight: 28,
              padding: 0,
              border: "none",
              background: "none",
              color: GRIS,
              fontSize: 11,
              textDecoration: "underline",
              textUnderlineOffset: 3,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            effacer la conversation
          </button>
        )}

        {/* LA RÉPONSE. ⚠️ Elle est rendue TELLE QUE LE SERVEUR L'A VALIDÉE :
          l'écran n'interprète pas, il affiche. Un registre inconnu n'arrive
          jamais ici — le serveur retombe sur du texte. */}
        {/* ── IL PARLE PENDANT QU'IL RÉFLÉCHIT ────────────────────────────
          La prose arrive par morceaux. Elle s'affiche telle quelle, puis la
          RÉPONSE VALIDÉE la remplace — jamais l'inverse. Une vue ou une porte
          n'apparaissent que dans cette réponse finale : l'écran ne bouge
          jamais sur une phrase à moitié arrivée. */}
        {/* ⚠️ CE QU'IL ANNONCE A REMONTÉ (2026-09-15) : ce qui presse se lit
          AVANT la conversation, pas entre une question et sa réponse. Le tri
          « ce qui a une maison y va » reste fait par l'accueil, lui seul sait
          ce qu'il affiche déjà. */}
        {gesteEvenement && gesteEvenement.geste ? (
          <div data-testid="operateur-evenement-geste">
            <CarteGesteDemande
              ref={carteEvenementRef}
              geste={gesteEvenement.geste}
              onFait={noterFait}
            />
          </div>
        ) : null}
        {feuilleEvenement && feuilleEvenement.feuille ? (
          <FeuilleOperateur
            description={feuilleEvenement.feuille}
            ton={feuilleEvenement.famille}
            // ⚠️ REFERMER SANS RIEN FAIRE NE TRAITE PAS L'ÉVÉNEMENT
            // (2026-09-21). Le fil sort la ligne dès l'ouverture — elle « vit
            // maintenant dans la feuille ». Si Dimitri referme pour plus tard,
            // la ligne doit REVENIR : sinon elle quitte le fil ET la pastille
            // pour la journée alors que la tâche est toujours à faire, et rien
            // ne le dit. C'est la feuille qui sait s'il en est parti un geste.
            onFermer={(sortie) => {
              if (!sortie || !sortie.gesteFait)
                if (feuilleEvenement.cle)
                  rouvrirEvenement(feuilleEvenement.cle);
              setFeuilleEvenement(null);
            }}
            onAller={(d) => {
              // Seules les portes vers un ÉCRAN sont câblées ici ; une vue
              // n'a pas encore d'hôte dans ce bloc (elle vit dans la réponse
              // conversée), et un bouton qui ne mène nulle part n'est pas
              // rendu — la feuille ne rend une porte de vue que si on lui
              // passe de quoi l'ouvrir.
              if (!d || !d.ecran) return;
              setFeuilleEvenement(null);
              onAller && onAller({ ecran: d.ecran, cible: d.cible || null });
            }}
          />
        ) : null}

        {!reponse && enTrain ? (
          <div
            data-testid="operateur-en-train"
            style={{
              fontSize: 12.5,
              color: NAVY_D,
              lineHeight: 1.55,
            }}
          >
            <p style={{ margin: 0 }}>{enTrain}</p>
          </div>
        ) : null}

        {reponse && (
          <div
            data-testid="operateur-reponse"
            style={{
              fontSize: 12.5,
              color: NAVY_D,
              lineHeight: 1.55,
            }}
          >
            {reponse.texte ? (
              <p style={{ margin: 0 }}>{reponse.texte}</p>
            ) : null}

            {/* CE QU'IL A RETENU — la seule trace visible d'une écriture qu'il
              a faite SANS clic. ⚠️ L'échec se DIT : une consigne qu'il croit
              avoir gardée et qui n'est nulle part, c'est exactement le défaut
              silencieux et plausible qu'on chasse depuis le début — Dimitri ne
              la redirait jamais, et elle n'agirait jamais. */}
            {reponse.retenir ? (
              <p
                data-testid="operateur-retenu"
                data-etat={
                  reponse.retenir.echec || reponse.retenir.refuse
                    ? "echec"
                    : "retenu"
                }
                style={{
                  margin: "8px 0 0",
                  fontSize: 11.5,
                  color:
                    reponse.retenir.echec || reponse.retenir.refuse
                      ? "#b45309"
                      : GRIS_D,
                }}
              >
                {reponse.retenir.refuse
                  ? `Je n'ai pas pu le retenir — ${reponse.retenir.refuse}.`
                  : reponse.retenir.echec
                    ? "Je n'ai pas réussi à le retenir — redites-le-moi."
                    : `\u2713 Retenu\u202f: \u00ab\u202f${reponse.retenir.texte}\u202f\u00bb`}
              </p>
            ) : null}

            {/* MONTRER — le titre vient du REGISTRE, pas du modèle : Dimitri
              doit voir ce qui a été COMPRIS, pas ce qui a été écrit.
              ⚠️ Le dessin REFUSE de dessiner quand les données ne tiennent pas
              debout : il rend une phrase qui le dit, jamais une ligne plate
              qui ressemblerait à une mesure. */}
            {reponse.registre === "montrer" && reponse.vue && (
              <div
                data-testid="operateur-vue"
                data-forme={reponse.vue.forme}
                data-etat={vueDonnees ? vueDonnees.etat : "absent"}
                style={{ marginTop: 9 }}
              >
                {vueDonnees && vueDonnees.etat === "en_cours" ? (
                  <p style={{ margin: "6px 0 0", opacity: 0.65 }}>
                    Je rassemble les chiffres…
                  </p>
                ) : (
                  <>
                    {/* ⚠️ Sans données, on passe `undefined` : le dessin REFUSE
                      alors de dessiner et rend une phrase. On ne lui donne
                      jamais un objet vide, qui produirait une grille plate
                      ressemblant à une vraie mesure. */}
                    <DessinVue
                      noms={noms}
                      // ⚠️ Le dessin rend une INTENTION ; c'est ici qu'on la
                      // traduit en écran. Un logement mène à sa fiche, un jour à
                      // la journée. Et comme partout : rien ne bouge à la voix.
                      onAller={(c) => {
                        if (!onAller || enVocalRef.current) return;
                        if (c.quoi === "ecran") {
                          // Une ligne de son journal : l'écran est une clé de
                          // la carte, vérifiée côté serveur avant de sortir.
                          onAller({ ecran: c.ecran, cible: c.cible || null });
                        } else if (c.quoi === "logement") {
                          onAller({ ecran: "logements", cible: c.logement_id });
                        } else {
                          onAller({ ecran: "menages", cible: c.date });
                        }
                      }}
                      // La croix d'une consigne : le levier EXISTANT
                      // (`operateur_memoire_decide` / `oublier`), puis on
                      // relit — jamais un retrait optimiste qui ferait croire
                      // à un oubli que la base a refusé.
                      onOublier={async (id) => {
                        const r = await oublierConsigne(supabase, id);
                        if (!r.ok) {
                          setVueDonnees((v) =>
                            v && v.etat === "prêt"
                              ? { ...v, partiel: r.motif }
                              : v,
                          );
                          return;
                        }
                        const d = await chargerDonneesVue(reponse.vue, {
                          supabase,
                        });
                        setVueDonnees(
                          d.ok
                            ? {
                                etat: "prêt",
                                donnees: d.donnees,
                                partiel: d.partiel,
                              }
                            : { etat: "absent", motif: d.motif },
                        );
                      }}
                      vue={reponse.vue}
                      donnees={
                        vueDonnees && vueDonnees.etat === "prêt"
                          ? vueDonnees.donnees
                          : undefined
                      }
                    />
                    {/* Le MOTIF de l'absence, nommé. « Rien » et « je ne sais
                      pas encore aller le chercher » ne sont pas la même
                      réponse, et Dimitri doit pouvoir les distinguer. */}
                    {vueDonnees && vueDonnees.etat === "absent" && (
                      <p
                        data-testid="operateur-vue-motif"
                        style={{
                          margin: "6px 0 0",
                          fontSize: 11.5,
                          opacity: 0.7,
                        }}
                      >
                        {vueDonnees.motif}
                      </p>
                    )}
                    {vueDonnees && vueDonnees.partiel && (
                      <p
                        data-testid="operateur-vue-partiel"
                        style={{
                          margin: "6px 0 0",
                          fontSize: 11.5,
                          opacity: 0.7,
                        }}
                      >
                        ⚠️{" "}
                        {reponse.vue.forme === "jours"
                          ? `Il manque ${vueDonnees.partiel} — la grille est incomplète.`
                          : vueDonnees.partiel}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* EMMENER, non sollicité — une porte, jamais un déplacement.
              « Je ne veux pas que mon écran change pendant que je lis. » */}
            {/* ── CE QU'IL A PRÉPARÉ ────────────────────────────────────
              ⚠️ La carte s'affiche AVEC la réponse, jamais à la place : il
              répond ET prépare, comme un bras droit. Et c'est le navigateur
              de Dimitri qui exécutera, avec son jeton — l'acteur audité est
              lui, jamais l'opérateur. */}
            {reponse.registre === "emmener" &&
              reponse.destination &&
              (reponse.destination.sollicite !== true || enVocal) && (
                <button
                  type="button"
                  data-testid="operateur-porte"
                  className="fbz-press"
                  onClick={() => onAller && onAller(reponse.destination)}
                  style={{
                    marginTop: 9,
                    minHeight: 30,
                    padding: "0 13px",
                    borderRadius: 15,
                    background: CIEL,
                    border: "none",
                    color: "#fff",
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Ouvrir {reponse.destination.ecran}
                </button>
              )}
          </div>
        )}

        {/* ── CE QU'IL A PRÉPARÉ ─────────────────────────────────────────
          ⚠️ LA CARTE EST HORS DU BLOC « RÉPONSE », ET C'EST TOUT L'OBJET DU
          LOT (2026-09-15). Elle vivait dans `reponse.geste` ; or
          `envoyerQuestion` commence par `setReponse(null)` — la carte
          disparaissait donc à l'instant où « oui » partait, c'est-à-dire
          exactement quand on en a besoin. Elle a maintenant son état propre,
          et se retrouve après un rechargement depuis le fil.

          ⚠️ Et c'est TOUJOURS le navigateur de Dimitri qui exécute, avec son
          jeton : que le déclencheur soit son clic ou son « oui », le chemin
          est le même (`carteRef.current.lancer()`), gardes comprises. */}
        {/* ── LA FEUILLE D'UN FORMULAIRE — AVANT la carte, jamais après ────
          Dimitri : « il MONTRE L'ENSEMBLE avant de valider. » L'ensemble se lit
          donc au-dessus du bouton, pas en dessous : ce qu'on relit après avoir
          cliqué ne protège plus de rien.

          ⚠️ Et quand la feuille porte une ALERTE, il n'y a pas de carte du
          tout — le serveur ne la compose même pas. On ne valide pas ce qu'on
          ne comprend pas. */}
        {reponse && reponse.feuille && (
          <FeuilleFormulaire feuille={reponse.feuille} />
        )}

        {carteAffichee && (
          <>
            {carteAffichee.rappel && (
              <p
                data-testid="operateur-carte-rappel"
                style={{
                  margin: "8px 0 0",
                  fontSize: 11.5,
                  color: GRIS_D,
                }}
              >
                {carteAffichee.rappel}
              </p>
            )}
            <CarteGesteDemande
              ref={carteRef}
              geste={carteAffichee.geste}
              onFait={noterFait}
            />
            {gestePerime && (
              <p
                data-testid="operateur-carte-perimee"
                style={{
                  margin: "6px 0 0",
                  fontSize: 11.5,
                  color: GRIS_D,
                }}
              >
                Cette carte a plus de dix minutes — « oui » ne la lance plus.
                Cliquez-la si vous la voulez toujours.
              </p>
            )}
          </>
        )}

        {/* Le champ ne fait plus semblant : il écoute. Ce bloc ne subsiste que
          pour le cas où l'envoi n'a pas encore eu lieu. */}
        {dit && !reponse && (
          <div
            data-testid="operateur-champ-inerte"
            style={{
              fontSize: 11,
              color: GRIS,
              lineHeight: 1.5,
            }}
          >
            Rien n&rsquo;a été envoyé, et votre texte est resté ici.
          </div>
        )}
      </div>
      {/* ── ON LUI PARLE ICI — EN BAS, SOUS LE FIL (maquette) ────────────
          C'est l'ordre d'une conversation : ce qu'on s'est dit au-dessus, ce
          qu'on écrit en dessous. L'inverse obligeait à remonter pour relire.
          `.opSep2` puis `.opSai2` : un trait, puis une carte blanche rayon 16
          dans une marge, boutons 40px rayon 13, envoi en dégradé ombré. */}
      <div
        aria-hidden="true"
        style={{ height: 1, background: BORD, margin: "0 17px" }}
      />
      <div
        data-testid="operateur-saisie"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "8px 12px 12px",
          background: "#fff",
          borderRadius: 16,
          padding: "5px 5px 5px 15px",
          boxShadow: `0 0 0 1px ${BORD}, 0 2px 8px rgba(35,50,64,.05)`,
        }}
      >
        <input
          data-testid="operateur-champ"
          value={brouillon}
          onChange={(e) => {
            setBrouillon(e.target.value);
            setDit(false);
          }}
          placeholder="Une question, une consigne…"
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 40,
            fontSize: 13.5,
            color: NAVY_D,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: DS.fontBody,
          }}
        />
        {/* ── LE MICRO — EN BAS, À CÔTÉ DE L'ENVOI (maquette) ───────────
            Dimitri, 2026-09-12 : « dans mon app réelle il est dans le bandeau.
            Dans ma maquette il est en bas, à côté du bouton d'envoi. Tu n'as
            pas appliqué la maquette sur ce point. » C'est vrai — et sa place
            est la bonne : parler et écrire sont le MÊME geste, celui d'envoyer
            quelque chose. Les séparer obligeait à viser en haut pour dire, en
            bas pour écrire.
            ⚠️ IL NE S'OUVRE JAMAIS TOUT SEUL. Un écran d'administration qui se
            met à écouter sans qu'on l'ait demandé est une catastrophe : en
            réunion, en clientèle, en open space. Un appui ouvre, un appui
            ferme — et quitter l'écran ferme aussi. */}
        <button
          type="button"
          data-testid="operateur-vocal"
          aria-pressed={enVocal}
          aria-label={
            enVocal
              ? "Fermer la conversation vocale"
              : "Parler avec l'opérateur"
          }
          onClick={basculerVocal}
          title={enVocal ? "En conversation" : "Parler"}
          style={{
            // `.opSai2 .m` — et `.m.rec` en terra quand il écoute.
            width: 40,
            height: 40,
            borderRadius: 13,
            border: "none",
            background: enVocal ? TERRA : FOND,
            color: enVocal ? "#fff" : GRIS,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 15,
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          🎙
        </button>
        <button
          data-testid="operateur-envoyer"
          onClick={parler}
          disabled={enCours || !brouillon.trim()}
          aria-label="Envoyer à l'opérateur"
          style={{
            // `.opSai2 .g` : dégradé ciel → #5d95cc, ombré.
            width: 40,
            height: 40,
            borderRadius: 13,
            background: `linear-gradient(135deg,${CIEL},#5d95cc)`,
            boxShadow: "0 2px 8px rgba(93,149,204,.32)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 14,
            border: "none",
            cursor: enCours || !brouillon.trim() ? "default" : "pointer",
            opacity: enCours || !brouillon.trim() ? 0.5 : 1,
          }}
        >
          {enCours ? "…" : "↑"}
        </button>
      </div>
    </div>
  );
}
